<template>
  <LoadingPage v-if="isLoading && !showSampleContractsModal"></LoadingPage>
  <div class="contract-container">
    <!-- Mobile drawer backdrop -->
    <div v-if="isMobileDrawerOpen" class="mobile-drawer-backdrop" @click="closeMobileDrawer"></div>
    <aside class="sidebar" v-if="!isEditModeEnabled" :class="{ 'collapsed': isSidebarCollapsed, 'mobile-drawer-open': isMobileDrawerOpen }">
      <div class="sidebar-logo">
        <img 
          :src="isSidebarCollapsed ? collapsedLogo : fullLogo" 
          :alt="isSidebarCollapsed ? 'Vertex Logo' : 'Vertex Cyber Security Logo'" 
          :class="['logo', { 'collapsed-logo': isSidebarCollapsed }]">
        <hr v-if="!isSidebarCollapsed" />
      </div>
      <button @click="toggleSidebar" class="collapse-btn">
        <span v-if="isSidebarCollapsed"><i class="fa-solid fa-chevron-right"></i></span>
        <span v-else><i class="fa-solid fa-chevron-left"></i></span>
      </button>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Organisation</h3>
        <p>{{ displayOrganisationName }}</p>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Organisation {{ businessNumberShortLabel }}</h3>
        <p>{{ displayAbn }}</p>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Client Lead Name</h3>
        <p>{{ (this.contract.Lead.first_name + " " + this.contract.Lead.last_name) || "No Lead Name" }}</p>
      </div>
      <div class="sidebar-section" v-if="contract.viewer_signer_name && !isSidebarCollapsed">
        <h3>Signing As</h3>
        <p style="font-weight: 600; color: #fff;">{{ contract.viewer_signer_name }}</p>
      </div>
      <div class="sidebar-section" v-else-if="visibleSharedUsers.length > 0">
        <h3 v-if="!isSidebarCollapsed">Signers</h3>
        <div class="shared-user-list" v-if="!isSidebarCollapsed">
          <div v-for="user in visibleSharedUsers" :key="user.email" class="shared-user-item">
            <span class="user-email" :title="user.email">{{ user.signer_name || user.email }}</span>
            <span class="user-role">{{ user.share_type }}</span>
          </div>
        </div>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Charity Status</h3>
        <div v-if="this.contract.Lead.is_charity" class="status-indicator active">Registered Charity</div>
        <div v-else class="status-indicator not-active">Non-Charity</div>
      </div>
    </aside>

    <main class="main-content" :class="{ 'expanded': isSidebarCollapsed || isEditModeEnabled, 'pdf-mode-active': isEditModeEnabled }">
      
      <!-- NEW WYSIWYG-style PDF Toolbar -->
      <div class="pdf-toolbar" v-if="isEditModeEnabled">
        <div class="pdf-toolbar-main-row">
          <span class="pdf-toolbar-mode-badge">
            <i class="fa-solid fa-pen-fancy"></i> Edit Mode
          </span>
          <button @click="showRevisionsModal = true" class="btn-primary revisions-btn" style="border-radius: 4px; position: relative;">
            <i class="fa-solid fa-list"></i> My Revisions ({{ pendingSectionRevisions.length }})
            <span v-if="totalUnreadCount > 0" title="Unread messages" style="position: absolute; top: -8px; right: -8px; background: #e53935; color: #fff; border-radius: 50%; min-width: 20px; height: 20px; font-size: 11px; display: flex; align-items: center; justify-content: center; padding: 0 4px;">{{ totalUnreadCount }}</span>
          </button>
          <button @click="exitEditMode" class="btn-undo">
            <i class="fa-solid fa-xmark"></i> Exit
          </button>
        </div>
        <div class="divider"></div>
        <div class="pdf-toolbar-actions-row">
          <span v-if="!toolbar.selectedText" class="toolbar-hint">
            <i class="fa-solid fa-arrow-pointer"></i> Highlight text to use annotation tools
          </span>
          <div v-else class="toolbar-action-buttons">
            <button @pointerdown.prevent @mousedown.prevent @click.prevent="openToolbarAction('strikethrough')" class="btn-strike">
              <i class="fa-solid fa-strikethrough"></i> Strike
            </button>
            <button @pointerdown.prevent @mousedown.prevent @click.prevent="openToolbarAction('comment')" class="btn-comment">
              <i class="fa-regular fa-comment"></i> Comment
            </button>
            <button @pointerdown.prevent @mousedown.prevent @click.prevent="openToolbarAction('strikethrough_comment')" class="btn-strike-comment">
              <i class="fa-solid fa-strikethrough"></i>+<i class="fa-regular fa-comment"></i> Strike &amp; Comment
            </button>
          </div>
        </div>
      </div>
      <header class="contract-header">
        <button class="mobile-menu-btn" @click="toggleMobileDrawer">
          <i class="fa-solid fa-bars"></i>
        </button>
        <div class="header-text">
          <h1>{{ this.contract.contract_title }}</h1>
        </div>
        <div class="header-actions" style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px; justify-content: flex-end;">
          <button 
            v-if="!isContractExpired && currentContractStatus !== 'declined' && !contract.is_accepted && contract.key_type !== 'internal' && contract.share_type !== 'viewer' && !contract.viewer_has_signed"
            class="button"
            :class="isEditModeEnabled ? 'secondary' : 'secondary'"
            style="min-width: 140px; display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 0; position: relative;"
            @click="toggleEditMode"
          >
            <i class="fas" :class="isEditModeEnabled ? 'fa-times' : 'fa-edit'"></i>
            {{ isEditModeEnabled ? 'Exit Edit Mode' : 'Suggest Edits' }}
            <span v-if="!isEditModeEnabled && totalUnreadCount > 0" title="Unread messages" style="position: absolute; top: -8px; right: -8px; background: #e53935; color: #fff; border-radius: 50%; min-width: 20px; height: 20px; font-size: 11px; display: flex; align-items: center; justify-content: center; padding: 0 4px;">{{ totalUnreadCount }}</span>
          </button>
          <button v-if="!isEditModeEnabled && !contract.is_accepted" class="button secondary" @click="downloadContractPdf" :hidden="IsHidden" :disabled="!contract.contract_title" style="margin-bottom: 0;">Download</button>
          <button v-if="!isContractExpired && currentContractStatus !== 'declined' && canAcceptContract && !this.contract.is_accepted && !isEditModeEnabled && !isContractInReview && !contract.viewer_has_signed && !contract.has_revision_not_sent" class="button primary" @click="startXodoSigning" :disabled="isConfirmingAuthority" style="margin-bottom: 0; display: inline-flex; align-items: center; justify-content: center; gap: 6px;">
            <i v-if="isConfirmingAuthority" class="fa-solid fa-circle-notch fa-spin"></i>
            <i v-else class="fa-solid fa-file-signature me-1"></i>
            {{ isConfirmingAuthority ? 'Redirecting to EverSign...' : 'Sign Contract in EverSign' }}
          </button>
          <span v-if="contract.viewer_has_signed && !this.contract.is_accepted" class="button" style="cursor: default; margin-bottom: 0; background-color: #2e7d32; color: #fff; border: none; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;" title="You have signed this contract"><i class="fa-solid fa-circle-check"></i> Signed</span>
          <span v-if="canAcceptContract && currentContractStatus !== 'declined' && !this.contract.is_accepted && contract.has_revision_not_sent && !isEditModeEnabled && !contract.viewer_has_signed" class="button" style="cursor: default; margin-bottom: 0; background-color: #f0ad4e; color: #fff; border: none; opacity: 0.9;" title="Signing is paused because new updates are pending. Please wait for the BDM to send the updated contract.">Pending Updates</span>
          <span v-if="canAcceptContract && currentContractStatus !== 'declined' && !this.contract.is_accepted && isContractInReview && !contract.has_revision_not_sent && !isEditModeEnabled && !contract.viewer_has_signed" class="button" style="cursor: default; margin-bottom: 0; background-color: #f0ad4e; color: #fff; border: none; opacity: 0.9;" title="Signing is paused while your revision comments are being reviewed">Under Review</span>
          
          <span v-if="currentContractStatus !== 'declined' && !this.contract.is_accepted && isAwaitingSignatures && !contract.viewer_has_signed" class="button" style="cursor: default; margin-bottom: 0; background-color: #6c757d; color: #fff; border: none; opacity: 0.9;" title="Waiting for all designated signatories to complete the document.">Awaiting Signatures</span>
          
          <span v-if="!contract.viewer_can_sign && !this.contract.is_accepted && !contract.viewer_has_signed && contract.share_type !== 'viewer' && contract.key_type !== 'internal'" class="button" style="cursor: default; margin-bottom: 0; background-color: #f0ad4e; color: #fff; border: none; opacity: 0.9;" title="Waiting for previous signers in the sequence to finish.">Awaiting Sequence</span>
          
          <span v-if="this.contract.is_accepted" class="button accepted-contract" style="cursor: default; margin-bottom: 0;">Executed</span>
          <span v-if="currentContractStatus === 'declined'" class="button declined-contract" style="cursor: default; margin-bottom: 0; background-color: #EB1C24; color: #fff; border: none; opacity: 0.9;">Declined</span>
        </div>
        <!-- Authority to Sign Checkbox -->
        <div v-if="!isContractExpired && currentContractStatus !== 'declined' && canAcceptContract && !contract.is_accepted && !isEditModeEnabled && !isContractInReview && !contract.viewer_has_signed && !contract.has_revision_not_sent" class="authority-confirmation">
          <label class="authority-checkbox-label">
            <input type="checkbox" v-model="authorityConfirmed" class="authority-checkbox" @change="showAuthorityWarning = !authorityConfirmed" />
            <span class="authority-text">I confirm that I am authorised to sign this contract on behalf of <strong class="authority-org-name">{{ displayOrganisationName }}</strong>.</span>
          </label>
          <p v-if="showAuthorityWarning" class="authority-warning"><i class="fa-solid fa-triangle-exclamation me-1"></i>Please confirm you are authorised to sign before proceeding.</p>
        </div>
      </header>

      <!-- Executed Notification Banner -->
      <div v-if="contract.is_accepted" class="alert-banner executed-banner" style="background-color: #e8f5e9; border-left: 5px solid #2e7d32; padding: 15px; margin-bottom: 20px; border-radius: 4px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); text-align: left; flex-wrap: wrap;">
        <div style="display: flex; align-items: center; gap: 12px; min-width: 280px; flex: 1;">
          <i class="fa-solid fa-circle-check" style="color: #2e7d32; font-size: 24px; flex-shrink: 0;"></i>
          <div>
            <strong style="color: #1b5e20; display: block; margin-bottom: 2px;">Contract Fully Executed</strong>
            <span style="color: #2e7d32; font-size: 13px;">This contract has been signed and is fully executed. Full signature records, IP details, and EverSign signing certificates are contained in the downloadable finalised PDF.</span>
          </div>
        </div>
        <button class="button primary" @click="downloadContractPdf" style="margin: 0; min-width: 140px; background-color: #2e7d32; border-color: #2e7d32; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-weight: bold; color: white;">
          <i class="fa-solid fa-download"></i> Download PDF
        </button>
      </div>

      <!-- Viewer Signed Notification Banner (Top) -->
      <div v-if="contract.viewer_has_signed && !contract.is_accepted" class="alert-banner" style="background-color: #e8f5e9; border-left: 5px solid #2e7d32; padding: 15px 20px; margin-bottom: 20px; border-radius: 4px; display: flex; align-items: center; gap: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); text-align: left;">
        <i class="fa-solid fa-circle-check" style="color: #2e7d32; font-size: 24px; flex-shrink: 0;"></i>
        <div>
          <strong style="color: #1b5e20; display: block; margin-bottom: 2px;">You Have Signed This Contract</strong>
          <span style="color: #2e7d32; font-size: 13px;">Your signature has been recorded. The document will be finalized once all signatories have completed their signatures.</span>
        </div>
      </div>

      <!-- Unsent Changes Banner -->
      <div v-if="contract.has_revision_not_sent" class="alert-banner" style="background-color: #fff9c4; border-left: 5px solid #fbc02d; padding: 15px; margin-bottom: 20px; border-radius: 4px; display: flex; align-items: center; gap: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); text-align: left;">
        <i class="fa-solid fa-triangle-exclamation" style="color: #f57f17; font-size: 20px; flex-shrink: 0;"></i>
        <div>
          <strong style="color: #5d4037; display: block; margin-bottom: 2px;">Contract Pending Updates</strong>
          <span style="color: #795548; font-size: 13px;">The BDM has unresolved updates or suggested changes that have not yet been finalised. Signing has been temporarily paused until the new version is sent.</span>
        </div>
      </div>

        <!-- ========== NEW JSON-BASED CONTRACT SECTIONS ========== -->
        <!-- Rendered from contract_sections_json, ordered by sequence, supporting reorder in draft mode -->
        <div v-if="parsedContractSections.length > 0" class="contract-sections-wrapper" :key="reRenderKey">

          <!-- Draft-mode reorder hint -->
          <div v-if="isDraftMode && canEditSections" class="reorder-hint">
            <i class="fa-solid fa-arrows-up-down"></i> Drag sections to reorder — changes save automatically
          </div>

          <div
            v-for="(section, idx) in orderedContractSections"
            :key="section.type + '-' + idx"
            class="contract-section-block"
            :class="{
              'section-hidden': section.is_hidden,
              'section-has-revision': sectionHasPendingRevision(section.type),
              'section-restricted': isEditModeEnabled && isRestrictedSection(section.type)
            }"
            :draggable="isDraftMode && canEditSections"
            :data-section-type="section.type"
            @dragstart="onDragStart($event, idx)"
            @dragover.prevent="onDragOver($event, idx)"
            @drop="onDrop($event, idx)"
          >
            <!-- Drag handle (draft mode only) -->
            <div v-if="isDraftMode && canEditSections" class="drag-handle" title="Drag to reorder">
              <i class="fa-solid fa-grip-lines"></i>
            </div>

            <!-- Section content -->
            <div class="section-content-wrapper">
              <!-- Hidden badge -->
              <div v-if="section.is_hidden" class="section-hidden-badge">
                <i class="fa-solid fa-eye-slash"></i> Deletion Requested
              </div>

              <!-- Pending revision badge -->
              <div v-if="sectionHasPendingRevision(section.type)" class="section-revision-badge">
                <i class="fa-solid fa-clock-rotate-left"></i> Under Review
              </div>

              <!-- Strikethrough overlay when strike has been submitted -->
              <template v-if="section.content && section.content.includes('{pricing_schedule_table}')">
                <div v-html="section.content.split('{pricing_schedule_table}')[0]" class="section-html-content"></div>
                
                <!-- BEGIN INJECTED PRICING TABLE -->
          <section id="pricing_table_section" v-if="this.filteredContractItems.length > 0 || this.acceptedContractItems.length > 0">

            <div v-if="!contract.is_accepted">
              <div class="tab-wrapper" v-if="this.filteredContractItems.length > 1">
                <h4>Product Pricing</h4>
                <ul class="nav nav-tabs" role="tablist">
                  <li v-for="(pi, index) in this.filteredContractItems" :key="index" class="nav-item" role="presentation">
                    <button class="nav-link" :class="{ 'active': pi.option_index == 1 }" :id="pi.option_index + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + pi.option_index + '-panel'" type="button" role="tab" :aria-controls="pi.option_index + '-panel'" :aria-selected="(pi.option_index == 1) ? 'true' : 'false'">
                      {{ (pi.option_index == 1) ? 'Main Option' : 'Option ' + toOptionName(pi.option_index) }}
                    </button>
                  </li>
                </ul>
                <div class="tab-content">
                  <div v-for="(pi, index) in this.filteredContractItems" :key="index" class="tab-pane fade" :class="{ 'show active': pi.option_index == 1 }" :id="pi.option_index + '-panel'" role="tabpanel" :aria-labelledby="pi.option_index + '-tab'">
                    <table class="product-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Description</th>
                          <th v-if="hasNotes(pi.productList)">Notes</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th v-if="contract.show_discount_calculation">Item Discount</th>
                          <th v-if="contract.show_discount_calculation">Total Discount</th>
                          <th>Total AUD</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(item, itemIndex) in pi.productList" :key="itemIndex">
                          <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                            <td :colspan="(contract.show_discount_calculation ? 7 : 5) + (hasNotes(pi.productList) ? 1 : 0)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                              {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                            </td>
                          </tr>
                          <tr v-else>
                            <td><strong>{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong></td>
                            <td>{{ item.description }}</td>
                            <td v-if="hasNotes(pi.productList)">{{ item.remarks }}</td>
                            <td><span>{{ item.quantity }}</span></td>
                            <td>
                              <span v-if="!contract.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                              <span v-else>{{ formatCurrency(item.price) }}</span>
                            </td>
                            <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount) }}</span></td>
                            <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount * item.quantity) }}</span></td>
                            <td>{{ formatCurrency(calculateTotal(item)) }}</td>
                          </tr>
                        </template>
                        <tr class="total-row">
                          <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(pi.productList) ? 1 : 0)"></td>
                          <td class="total-label">Subtotal</td>
                          <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(pi.productList)) }}</td>
                        </tr>
                        <tr class="total-row">
                          <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(pi.productList) ? 1 : 0)"></td>
                          <td title="Goods and Services Tax" class="total-label">GST (10%)</td>
                          <td class="total-amount">{{ formatCurrency(calculateOptionGST(pi.productList)) }}</td>
                        </tr>
                        <tr class="total-row">
                          <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(pi.productList) ? 1 : 0)"></td>
                          <td class="total-label">Total AUD</td>
                          <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(pi.productList)) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="simple-pricing-wrapper" v-else-if="this.filteredContractItems.length > 0">
                <h4>Product Pricing</h4>
                <table class="product-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th v-if="hasNotes(this.filteredContractItems[0].productList)">Notes</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th v-if="contract.show_discount_calculation">Item Discount</th>
                      <th v-if="contract.show_discount_calculation">Total Discount</th>
                      <th>Total AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(item, itemIndex) in this.filteredContractItems[0].productList" :key="itemIndex">
                      <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                        <td :colspan="(contract.show_discount_calculation ? 7 : 5) + (hasNotes(this.filteredContractItems[0].productList) ? 1 : 0)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                          {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                        </td>
                      </tr>
                      <tr v-else>
                        <td><strong>{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong></td>
                        <td>{{ item.description }}</td>
                        <td v-if="hasNotes(this.filteredContractItems[0].productList)">{{ item.remarks }}</td>
                        <td><span>{{ item.quantity }}</span></td>
                        <td>
                          <span v-if="!contract.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                          <span v-else>{{ formatCurrency(item.price) }}</span>
                        </td>
                        <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount) }}</span></td>
                        <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount * item.quantity) }}</span></td>
                        <td>{{ formatCurrency(calculateTotal(item)) }}</td>
                      </tr>
                    </template>
                    <tr class="total-row">
                      <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(this.filteredContractItems[0].productList) ? 1 : 0)"></td>
                      <td class="total-label">Subtotal</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(this.filteredContractItems[0].productList)) }}</td>
                    </tr>
                    <tr class="total-row">
                      <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(this.filteredContractItems[0].productList) ? 1 : 0)"></td>
                      <td title="Goods and Services Tax" class="total-label">GST (10%)</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionGST(this.filteredContractItems[0].productList)) }}</td>
                    </tr>
                    <tr class="total-row">
                      <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(this.filteredContractItems[0].productList) ? 1 : 0)"></td>
                      <td class="total-label">Total AUD</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(this.filteredContractItems[0].productList)) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-else-if="contract.is_accepted && acceptedContractItems.length > 0" class="simple-pricing-wrapper">
              <h4>Accepted Products & Services</h4>
              <table class="product-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Description</th>
                    <th v-if="hasNotes(acceptedContractItems[0].productList)">Notes</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th v-if="contract.show_discount_calculation">Item Discount</th>
                    <th v-if="contract.show_discount_calculation">Total Discount</th>
                    <th>Total AUD</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(item, itemIndex) in acceptedContractItems[0].productList" :key="itemIndex">
                    <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                      <td :colspan="(contract.show_discount_calculation ? 7 : 5) + (hasNotes(acceptedContractItems[0].productList) ? 1 : 0)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                        {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                      </td>
                    </tr>
                    <tr v-else>
                      <td><strong>{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong></td>
                      <td>{{ item.description }}</td>
                      <td v-if="hasNotes(acceptedContractItems[0].productList)">{{ item.remarks }}</td>
                      <td><span>{{ item.quantity }}</span></td>
                      <td>
                        <span v-if="!contract.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                        <span v-else>{{ formatCurrency(item.price) }}</span>
                      </td>
                      <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount) }}</span></td>
                      <td v-if="contract.show_discount_calculation"><span>{{ formatCurrency(item.discount * item.quantity) }}</span></td>
                      <td>{{ formatCurrency(calculateTotal(item)) }}</td>
                    </tr>
                  </template>
                  <tr class="total-row">
                    <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(acceptedContractItems[0].productList) ? 1 : 0)"></td>
                    <td class="total-label">Subtotal</td>
                    <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(acceptedContractItems[0].productList)) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(acceptedContractItems[0].productList) ? 1 : 0)"></td>
                    <td title="Goods and Services Tax" class="total-label">GST (10%)</td>
                    <td class="total-amount">{{ formatCurrency(calculateOptionGST(acceptedContractItems[0].productList)) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td :colspan="(contract.show_discount_calculation ? 5 : 3) + (hasNotes(acceptedContractItems[0].productList) ? 1 : 0)"></td>
                    <td class="total-label">Total AUD</td>
                    <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(acceptedContractItems[0].productList)) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="canAcceptAdditionalItems && canAcceptContract && !isContractInReview" class="additional-actions primary" style="flex-direction: column; align-items: flex-end; gap: 12px;">
                <label class="authority-checkbox-label" v-if="!authorityConfirmed" style="margin: 0;">
                  <input type="checkbox" v-model="authorityConfirmed" class="authority-checkbox" @change="showAuthorityWarning = !authorityConfirmed" />
                  <span class="authority-text" style="font-size: 13px;">I confirm that I am authorised to sign this contract on behalf of <strong class="authority-org-name">{{ displayOrganisationName }}</strong>.</span>
                </label>
                <p v-if="showAuthorityWarning && !authorityConfirmed" class="authority-warning" style="margin: 0; text-align: right;"><i class="fa-solid fa-triangle-exclamation me-1"></i>Please confirm you are authorised to sign before proceeding.</p>
                <button class="button primary" @click="startXodoSigning" :disabled="isConfirmingAuthority" style="display: inline-flex; align-items: center; justify-content: center; gap: 6px;">
                  <i v-if="isConfirmingAuthority" class="fa-solid fa-circle-notch fa-spin"></i>
                  <i v-else class="fa-solid fa-file-signature me-1"></i>
                  {{ isConfirmingAuthority ? 'Redirecting...' : 'Accept Additional Product & Services' }}
                </button>
              </div>
            </div>

          </section>

                <!-- END INJECTED PRICING TABLE -->

                <div v-html="section.content.split('{pricing_schedule_table}')[1]" class="section-html-content"></div>
              </template>
              <template v-else>
                <div
                  v-html="section.content"
                  class="section-html-content section-html-standard"
                ></div>
              </template>

              <!-- Injected Signing Actions for Execution Page -->
              <div
                v-if="section.type === 'ExecutionPage' && !isEditModeEnabled && !isContractExpired && currentContractStatus !== 'declined' && canAcceptContract && !contract.is_accepted && !isContractInReview && !contract.viewer_has_signed && !contract.has_revision_not_sent"
                class="execution-page-signing-block"
              >
                <!-- Authority to Sign Checkbox -->
                <div class="authority-confirmation footer-authority-box">
                  <label class="authority-checkbox-label">
                    <input
                      type="checkbox"
                      v-model="authorityConfirmed"
                      class="authority-checkbox"
                      @change="showAuthorityWarning = !authorityConfirmed"
                    />
                    <span class="authority-text">
                      I confirm that I am authorised to sign this contract on behalf of <strong class="authority-org-name">{{ displayOrganisationName }}</strong>.
                    </span>
                  </label>
                  <p v-if="showAuthorityWarning" class="authority-warning">
                    <i class="fa-solid fa-triangle-exclamation me-1"></i>Please confirm you are authorised to sign before proceeding.
                  </p>
                </div>

                <!-- Sign Button -->
                <div class="footer-signing-action-row">
                  <button
                    class="button primary footer-sign-btn"
                    @click="startXodoSigning"
                    :disabled="isConfirmingAuthority"
                  >
                    <i v-if="isConfirmingAuthority" class="fa-solid fa-circle-notch fa-spin"></i>
                    <i v-else class="fa-solid fa-file-signature me-1"></i>
                    {{ isConfirmingAuthority ? 'Redirecting to EverSign...' : 'Sign Contract in EverSign' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Hover action bar -->
            <div class="section-action-bar" v-if="(canEditSections || canClientSuggestRevision) && !section.is_hidden && !isRestrictedSection(section.type)">
              <button class="section-action-btn revision" @click="openSectionAction(section, 'revision')" title="Suggest a revision">
                <i class="fa-solid fa-pen-to-square"></i> Suggest Revision
              </button>
            </div>
          </div>
        </div>

        <!-- ========== BOTTOM SIGNING & EXECUTION SECTION ========== -->
        <div class="contract-footer-signing-container" v-if="!isEditModeEnabled">
          <!-- Fallback Authority & Sign Button Block (if contract has no ExecutionPage block) -->
          <div
            v-if="!hasExecutionPageSection && !isContractExpired && currentContractStatus !== 'declined' && canAcceptContract && !contract.is_accepted && !isContractInReview && !contract.viewer_has_signed && !contract.has_revision_not_sent"
            class="execution-page-signing-block"
          >
            <!-- Authority to Sign Checkbox -->
            <div class="authority-confirmation footer-authority-box">
              <label class="authority-checkbox-label">
                <input
                  type="checkbox"
                  v-model="authorityConfirmed"
                  class="authority-checkbox"
                  @change="showAuthorityWarning = !authorityConfirmed"
                />
                <span class="authority-text">
                  I confirm that I am authorised to sign this contract on behalf of <strong class="authority-org-name">{{ displayOrganisationName }}</strong>.
                </span>
              </label>
              <p v-if="showAuthorityWarning" class="authority-warning">
                <i class="fa-solid fa-triangle-exclamation me-1"></i>Please confirm you are authorised to sign before proceeding.
              </p>
            </div>

            <!-- Sign Button -->
            <div class="footer-signing-action-row">
              <button
                class="button primary footer-sign-btn"
                @click="startXodoSigning"
                :disabled="isConfirmingAuthority"
              >
                <i v-if="isConfirmingAuthority" class="fa-solid fa-circle-notch fa-spin"></i>
                <i v-else class="fa-solid fa-file-signature me-1"></i>
                {{ isConfirmingAuthority ? 'Redirecting to EverSign...' : 'Sign Contract in EverSign' }}
              </button>
            </div>
          </div>

          <!-- Fully Executed Status -->
          <div
            v-else-if="contract.is_accepted"
            class="alert-banner executed-banner footer-banner"
          >
            <div style="display: flex; align-items: center; gap: 12px; min-width: 280px; flex: 1;">
              <i class="fa-solid fa-circle-check" style="color: #2e7d32; font-size: 24px; flex-shrink: 0;"></i>
              <div>
                <strong style="color: #1b5e20; display: block; margin-bottom: 2px;">Contract Fully Executed</strong>
                <span style="color: #2e7d32; font-size: 13px;">This contract has been signed and is fully executed. Full signature records, IP details, and EverSign signing certificates are contained in the downloadable finalised PDF.</span>
              </div>
            </div>
            <button
              class="button primary"
              @click="downloadContractPdf"
              style="margin: 0; min-width: 140px; background-color: #2e7d32; border-color: #2e7d32; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-weight: bold; color: white;"
            >
              <i class="fa-solid fa-download"></i> Download PDF
            </button>
          </div>

          <!-- Pending Updates Status -->
          <div
            v-else-if="canAcceptContract && currentContractStatus !== 'declined' && !contract.is_accepted && contract.has_revision_not_sent"
            class="alert-banner footer-banner"
            style="background-color: #fff9c4; border-left: 5px solid #fbc02d;"
          >
            <i class="fa-solid fa-triangle-exclamation" style="color: #f57f17; font-size: 20px; flex-shrink: 0;"></i>
            <div>
              <strong style="color: #5d4037; display: block; margin-bottom: 2px;">Contract Pending Updates</strong>
              <span style="color: #795548; font-size: 13px;">The BDM has unresolved updates or suggested changes that have not yet been finalised. Signing has been temporarily paused until the new version is sent.</span>
            </div>
          </div>

          <!-- Under Review Status -->
          <div
            v-else-if="canAcceptContract && currentContractStatus !== 'declined' && !contract.is_accepted && isContractInReview"
            class="alert-banner footer-banner"
            style="background-color: #fff9c4; border-left: 5px solid #fbc02d;"
          >
            <i class="fa-solid fa-clock-rotate-left" style="color: #f57f17; font-size: 20px; flex-shrink: 0;"></i>
            <div>
              <strong style="color: #5d4037; display: block; margin-bottom: 2px;">Contract Under Review</strong>
              <span style="color: #795548; font-size: 13px;">Signing is paused while your revision comments and suggested edits are being reviewed.</span>
            </div>
          </div>

          <!-- Awaiting Signatures Status -->
          <div
            v-else-if="currentContractStatus !== 'declined' && !contract.is_accepted && isAwaitingSignatures"
            class="alert-banner footer-banner"
            style="background-color: #f8f9fa; border-left: 5px solid #6c757d;"
          >
            <i class="fa-solid fa-users" style="color: #6c757d; font-size: 20px; flex-shrink: 0;"></i>
            <div>
              <strong style="color: #495057; display: block; margin-bottom: 2px;">Awaiting Signatures</strong>
              <span style="color: #6c757d; font-size: 13px;">Waiting for all designated signatories to complete the document.</span>
            </div>
          </div>

          <!-- Awaiting Sequence Status -->
          <div
            v-else-if="!contract.viewer_can_sign && !contract.is_accepted && !contract.viewer_has_signed && contract.share_type !== 'viewer' && contract.key_type !== 'internal'"
            class="alert-banner footer-banner"
            style="background-color: #fff9c4; border-left: 5px solid #fbc02d;"
          >
            <i class="fa-solid fa-list-ol" style="color: #f57f17; font-size: 20px; flex-shrink: 0;"></i>
            <div>
              <strong style="color: #5d4037; display: block; margin-bottom: 2px;">Awaiting Sequence</strong>
              <span style="color: #795548; font-size: 13px;">Waiting for previous signers in the designated sequence to finish signing.</span>
            </div>
          </div>
        </div>
    </main>



    <aside class="right-sidebar" v-if="!isEditModeEnabled">

      <div class="top-actions" v-if="!isContractExpired && currentContractStatus !== 'declined' && canAcceptContract && !this.contract.is_accepted && !isContractInReview && !contract.has_revision_not_sent">
        <button 
          v-if="!contract.viewer_has_signed"
          class="button primary sign-contract-btn" 
          @click="startXodoSigning" 
          :disabled="isConfirmingAuthority"
        >
          <i v-if="isConfirmingAuthority" class="fa-solid fa-circle-notch fa-spin"></i>
          <i v-else class="fa-solid fa-file-signature me-1"></i>
          {{ isConfirmingAuthority ? 'Redirecting to EverSign...' : 'Sign Contract in EverSign' }}
        </button>
        <div 
          v-else-if="contract.viewer_has_signed"
          class="signed-status-sidebar-badge"
          style="background: rgba(46, 125, 50, 0.2); border: 1px solid #2e7d32; color: #a5d6a7; padding: 10px 14px; border-radius: 6px; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; text-align: center;"
        >
          <i class="fa-solid fa-circle-check" style="color: #2cce7d; font-size: 16px;"></i>
          <span style="color: #fff;">You Have Signed</span>
        </div>
      </div>

      <div class="sidebar-section">
        <h3>Vertex Lead</h3>
        <p>{{ this.vertex_bdm || this.contract.contract_owner || "Vertex Cyber Security" }}</p>
      </div>
      <div class="sidebar-section">
        <h3>Contact Number</h3>
        <p>{{ this.vertex_bdm_mobile || this.contract.contract_owner_contact || "1300 229 237" }}</p>
      </div>
      <div class="sidebar-section">
        <h3>Contact Email</h3>
        <p>{{ this.vertex_bdm_email || this.contract.contract_owner_email || "martin@vtxsecurity.com" }}</p>
      </div>
      
      <div class="sidebar-section">
        <h3 v-if="currentContractStatus === 'accepted' || currentContractStatus === 'partially_accepted'">Execution Date</h3>
        <h3 v-else-if="currentContractStatus === 'declined'">Date Declined</h3>
        <h3 v-else>Contract Expiry Date</h3>
        
        <p v-if="currentContractStatus === 'accepted' || currentContractStatus === 'partially_accepted'">{{ executionDateDisplay }}</p>
        <p v-else-if="currentContractStatus === 'declined'">{{ declinedDateDisplay }}</p>
        <p v-else-if="!this.contract.is_accepted && (DaysToAcceptCounter(this.contract.expiry_date) < -1000)">Not Set</p>
        <p v-else>{{ formatUtcToTimeZone(this.contract.expiry_date) }}</p>

        <div v-if="currentContractStatus === 'partially_accepted'" class="status-indicator accepted">
          Partially Executed
        </div>
        <div v-else-if="currentContractStatus === 'accepted'" class="status-indicator accepted">
          Executed
        </div>
        <div v-else-if="currentContractStatus === 'declined'" class="status-indicator declined">
          Declined
        </div>
        <div v-else-if="currentContractStatus === 'expired'" class="status-indicator expired">
          Contract Expired
        </div>
        <div v-else class="status-indicator pending">
          <span v-if="(DaysToAcceptCounter(this.contract.expiry_date) < -1000)">Countdown TBD</span>
          <span v-else>
            {{ DaysToAcceptCounter(this.contract.expiry_date) }} {{ DaysToAcceptCounter(this.contract.expiry_date) == 1 ? 'day' : 'days' }} left
          </span>
        </div>
      </div>

      <div class="sidebar-section" v-if="contract.contract_acceptance && contract.contract_acceptance.signers && contract.contract_acceptance.signers.length > 0">
        <h3>Signers</h3>
        <div style="display: flex; flex-direction: column; gap: 12px; margin-top: 8px;">
          <div style="font-size: 13px; color: #fff; display: flex; align-items: flex-start; gap: 8px;">
            <i class="fa-solid fa-user-check" style="color: #2cce7d; font-size: 14px; margin-top: 2px;"></i>
            <div>
              <div style="font-weight: 600;">{{ this.contract_settings?.signatory_name || this.vertex_bdm || 'Martin Boyd' }}</div>
              <div style="font-size: 11px; color: #aaa;">Vertex Representative (Signed)</div>
            </div>
          </div>
          <div v-for="signer in contract.contract_acceptance.signers" :key="signer.email" style="font-size: 13px; color: #fff; display: flex; align-items: flex-start; gap: 8px;">
            <i v-if="signer.has_signed" class="fa-solid fa-circle-check" style="color: #2cce7d; font-size: 14px; margin-top: 2px;"></i>
            <i v-else class="fa-solid fa-clock" style="color: #ff9800; font-size: 14px; margin-top: 2px;"></i>
            <div>
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="font-weight: 600;">{{ [signer.first_name || signer.firstName, signer.last_name || signer.lastName].filter(Boolean).join(' ') || signer.name }}</span>
                <span v-if="signer.role === 'Primary Signatory'" style="font-size: 9px; background: rgba(0, 160, 193, 0.2); color: #00bcd4; border: 1px solid #00a0c1; padding: 1px 4px; border-radius: 3px; font-weight: 700; text-transform: uppercase;">Primary</span>
              </div>
              <div style="font-size: 11px; color: #aaa;">{{ signer.email }}</div>
              <div v-if="signer.has_signed && signer.signed_at" style="font-size: 10px; color: #7f8c8d; margin-top: 2px; font-style: italic;">
                Signed on: {{ formatSignDate(signer.signed_at) }}
              </div>
              <div v-else-if="signer.has_signed" style="font-size: 10px; color: #7f8c8d; margin-top: 2px; font-style: italic;">
                ✓ Signed
              </div>
              <div v-else style="font-size: 10px; color: #ff9800; margin-top: 2px; font-weight: 500;">
                Awaiting Signature
              </div>
              <div v-if="!signer.has_signed && activeUnsignedSigner && activeUnsignedSigner.email === signer.email && contract.contract_acceptance && contract.contract_acceptance.all_to_sign" style="margin-top: 6px;">
                <button 
                  @click="resendCurrentHandoff" 
                  :disabled="isResendingHandoff"
                  style="padding: 3px 8px; font-size: 10px; border-radius: 4px; border: 1px solid rgba(0, 188, 212, 0.4); background: rgba(0, 188, 212, 0.05); color: #00bcd4; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 4px; font-weight: 600;"
                  onmouseover="this.style.background='rgba(0, 188, 212, 0.15)'; this.style.borderColor='#00bcd4';"
                  onmouseout="this.style.background='rgba(0, 188, 212, 0.05)'; this.style.borderColor='rgba(0, 188, 212, 0.4)';"
                >
                  <i v-if="isResendingHandoff" class="fa-solid fa-spinner fa-spin"></i>
                  <i v-else class="fa-solid fa-paper-plane"></i>
                  Resend Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="sidebar-section" v-else-if="contract.is_accepted || currentContractStatus === 'accepted' || currentContractStatus === 'partially_accepted'">
        <h3>Signers</h3>
        <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 5px;">
          <div style="font-size: 13px; color: #fff; display: flex; align-items: flex-start; gap: 8px;">
            <i class="fa-solid fa-user-check" style="color: #2e7d32; font-size: 14px; margin-top: 2px;"></i>
            <div>
              <div style="font-weight: 600;">{{ this.contract_settings?.signatory_name || this.vertex_bdm || 'Martin Boyd' }}</div>
              <div style="font-size: 11px; color: #aaa;">Vertex Representative</div>
            </div>
          </div>
          <div v-for="signer in executedSigners" :key="signer.email" style="font-size: 13px; color: #fff; display: flex; align-items: flex-start; gap: 8px;">
            <i class="fa-solid fa-user-check" style="color: #2e7d32; font-size: 14px; margin-top: 2px;"></i>
            <div>
              <div style="font-weight: 600;">{{ [signer.first_name || signer.firstName, signer.last_name || signer.lastName].filter(Boolean).join(' ') || signer.name }}</div>
              <div style="font-size: 11px; color: #aaa;">{{ signer.email }}</div>
            </div>
          </div>
        </div>
      </div>

    </aside>

    <ShareContractModal
    :show="showShareModal"
    :existing-shared-users="contract.shared_users"
    :contract-access-key="this.access_key"
    :contract-unique-key="this.unique_key"
    :lead-email="contract.Lead.email"
    @close="closeShareModal"
    @share-successful="handleShareSuccess"
    @revoke-successful="handleShareRevoked"
    @share-failed="handleShareFailure"
    />

    <AskQuestionModal
      :show="showAskQuestionModal"
      :contract-unique-key="this.unique_key"
      :contract-access-key="this.access_key"
      :client-email="contract.Lead.email"
      @close="closeAskQuestionModal"
      @question-submitted="this.$toast.success('Your question has been sent successfully!')"
      @question-failed="this.$toast.error('Failed to submit question. Please try again.')"
    />



    <div v-if="showMeetModal" class="modal-overlay" @click.self="closeMeetModal">
      <div class="modal-content">
        <button class="modal-close-btn" @click="closeMeetModal">&times;</button>
        <h3>Book a Call</h3>
        <iframe
          :src="vertex_bdm_calendar_link"
          style="width: 100%; height: 600px; border: none;"
        ></iframe>
      </div>
    </div>

    <div v-if="showSampleContractsModal" class="modal-overlay" @click.self="closeSampleContractsModal">
      <div class="sample-contract-modal">
        <div class="modal-header-custom">
          <h5 class="modal-title-custom">Sample Contract Terms</h5>
          <button @click="closeSampleContractsModal" class="close-button-custom">&times;</button>
        </div>
        <div class="modal-controls">
          <input v-model.trim="sampleContractSearch" type="text" class="form-control form-control-sm"
            placeholder="🔍 Search..."
            style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
            @keyup="filterSampleContracts">
        </div>
        <div class="modal-body-custom">
          <div v-if="isModalLoading" class="no-results">Loading...</div>
          <ul v-else-if="filteredSampleContracts.length > 0" class="contract-list">
            <li v-for="contract in filteredSampleContracts" :key="contract.id" @click="viewContract(contract)" class="contract-item-card">
              <div class="contract-info">
                <p class="contract-name">{{ contract.name }}</p>
                <p class="contract-details"><strong>Type:</strong> {{ contract.contractType }}</p>
                <p class="contract-details"><strong>Version:</strong> {{ contract.version }}</p>
              </div>
            </li>
          </ul>
          <div v-else class="no-results">
            <p>No sample contracts found.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== SECTION ACTION PANEL ========== -->
    <div v-if="sectionActionPanel.show" class="modal-overlay" @click.self="closeSectionAction">
      <div class="section-action-modal">
        <button class="modal-close-btn" @click="closeSectionAction">&times;</button>

        <h3 class="section-action-title">
          <span v-if="sectionActionPanel.action === 'comment'"><i class="fa-regular fa-comment"></i> Add Comment</span>
          <span v-else><i class="fa-solid fa-pen-to-square"></i> Suggest a Revision</span>
        </h3>

        <p class="section-action-subtitle">
          Section: <strong>{{ sectionActionPanel.section?.type }}</strong>
        </p>



        <textarea
          v-model="sectionActionPanel.text"
          ref="actionTextarea"
          class="section-action-textarea"
          :placeholder="sectionActionPanel.action === 'comment' ? 'Type your comment here...' : sectionActionPanel.action === 'revision' ? 'Describe the changes you would like...' : 'Reason for deletion (required)...'"
          rows="6"
          :disabled="sectionActionPanel.isSubmitting || sectionActionPanel.submitted"
        ></textarea>

        <div class="section-action-footer">
          <button class="button secondary" @click="closeSectionAction" :disabled="sectionActionPanel.isSubmitting">Cancel</button>
          <button
            class="button primary"
            @click="submitSectionAction"
            :disabled="sectionActionPanel.isSubmitting || sectionActionPanel.submitted"
          >
            <span v-if="sectionActionPanel.isSubmitting"><i class="fa-solid fa-spinner fa-spin"></i> Submitting...</span>
            <span v-else-if="sectionActionPanel.submitted"><i class="fa-solid fa-circle-check"></i> Submitted!</span>
            <span v-else>Submit</span>
          </button>
        </div>
      </div>
    </div>



  <!-- Float toolbar replaced by fixed PDF toolbar -->
  <!--
  <div v-if="false" class="annotation-toolbar" :style="{ top: toolbar.y + 'px', left: toolbar.x + 'px' }">
  </div>
  -->

  <div v-if="toast.show" class="toast" :class="`toast-${toast.type}`">
    {{ toast.message }}
  </div>
    <!-- Client Revisions Deletion Tool -->
    <div class="modal-overlay" v-if="showRevisionsModal" @click.self="showRevisionsModal = false" style="z-index: 10000; position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center;">
      <div class="modal-content-custom" style="position: relative; background: white; padding: 2.5rem 2rem 2rem 2rem; border-radius: 16px; width: 600px; max-width: 90vw; max-height: 80vh; display: flex; flex-direction: column; box-shadow: 0 5px 15px rgba(0,0,0,0.3);">
          <button @click="showRevisionsModal = false" style="position: absolute; top: 1rem; right: 1rem; font-size: 2rem; font-weight: bold; line-height: 1; color: #888; background: none; border: none; cursor: pointer;">&times;</button>
          <h5 style="font-weight: bold; font-size: 1.25rem; text-align: left; margin-bottom: 0;">My Revisions ({{ pendingSectionRevisions.length }})</h5>
          <hr style="margin-top: 1rem; margin-bottom: 1.5rem;">
        
        <div class="modal-body revisions-list" style="padding: 0; overflow-y: auto;">
          <p v-if="pendingSectionRevisions.length === 0" style="color: #666;">No revisions created yet.</p>
          <div class="revision-item" v-for="rev in pendingSectionRevisions" :key="rev.id" style="border: 1px solid #eee; border-radius: 6px; padding: 15px; margin-bottom: 15px;">
            <div class="revision-content" style="margin-bottom: 12px;">
               <span v-if="rev.is_ignored" class="badge" style="background: #6c757d; color: white; padding: 3px 8px; border-radius: 4px; font-size: 12px; margin-right: 8px;">Ignored</span>
               <span v-else-if="rev.is_resolved" class="badge" style="background: #28a745; color: white; padding: 3px 8px; border-radius: 4px; font-size: 12px; margin-right: 8px;">Resolved</span>
               <span class="badge" style="background: #333; color: white; padding: 3px 8px; border-radius: 4px; font-size: 12px; margin-right: 8px;">{{ rev.action }}</span> 
               <span class="revision-section" style="font-weight: bold; color: #555;">{{ rev.section_name }}</span>
               <pre class="revision-text" style="background: #f9f9f9; padding: 10px; border-radius: 4px; border: 1px solid #e0e0e0; margin-top: 10px; white-space: pre-wrap; font-family: inherit; font-size: 13px;">{{ rev.content }}</pre>
            </div>
            
            <div style="display: flex; gap: 10px;">
                <button class="btn btn-sm btn-outline-primary" @click="toggleChat(rev.id)" style="position: relative;">
                   <i class="fa-regular fa-comments"></i> Chat Thread
                   <span v-if="revisionUnreadCounts[rev.id]" title="Unread messages" style="position: absolute; top: -6px; right: -6px; background: #e53935; color: #fff; border-radius: 50%; width: 18px; height: 18px; font-size: 10px; display: flex; align-items: center; justify-content: center;">{{ revisionUnreadCounts[rev.id] }}</span>
                </button>
                <button v-if="!rev.is_resolved && !rev.is_ignored" class="btn btn-sm btn-outline-danger" @click="deleteClientRevision(rev.id)" title="Delete Revision">
                  <i class="fa-solid fa-trash-can"></i> Delete Revision
                </button>
            </div>

            <!-- CHAT PANEL -->
            <div v-if="activeChatRevisionId === rev.id" style="margin-top: 15px; border-top: 1px solid #ddd; padding-top: 15px;">
               <div style="max-height: 250px; overflow-y: auto; margin-bottom: 10px; background: #fdfdfd; padding: 10px; border-radius: 6px;">
                  <p v-if="chatComments.length === 0" style="color: #888; font-size: 12px; text-align: center;">No messages yet. Start the conversation!</p>
                  
                  <div v-for="c in chatComments" :key="c.id" style="margin-bottom: 10px; padding: 8px; border-radius: 6px;" :style="{ background: c.is_internal ? '#e3f2fd' : '#f5f5f5', borderLeft: c.is_internal ? '3px solid #1976d2' : '3px solid #9e9e9e' }">
                     <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <strong style="font-size: 12px; color: #333;">{{ c.submitted_by }} <span v-if="c.is_internal" style="color: #1976d2; font-size: 10px;">(Vertex Team)</span></strong>
                        <span style="font-size: 10px; color: #888;">{{ new Date(c.created_at).toLocaleString() }}</span>
                     </div>
                     <p style="font-size: 13px; margin: 0; color: #444; white-space: pre-wrap;">{{ c.comment_text }}</p>
                  </div>
               </div>
               <div style="display: flex; flex-direction: column; gap: 8px;">
                  <textarea v-model="newCommentText" class="form-control" rows="2" placeholder="Type a reply..." style="font-size: 13px;"></textarea>
                  <button class="btn btn-primary btn-sm" style="align-self: flex-end;" :disabled="isSubmittingComment || !newCommentText.trim()" @click="postComment(rev.id)">
                     <i class="fa-solid fa-paper-plane"></i> Send Reply
                  </button>
               </div>
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>

  <ConfirmModal
     v-model="showConfirmModal"
     :title="confirmModalTitle"
     :message="confirmModalMessage"
     @confirm="onConfirmAction"
  />
</template>

<script>

// Your existing imports
import { isEmpty, toOptionName } from '../../../plugins/helper';
import { getBusinessNumberShortLabel } from '@/plugins/businessValidator.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
import ShareContractModal from './ShareContract.vue';
import AskQuestionModal from './AskQuestionModal.vue';

import VLogo from '@/assets/img/V.svg';
import VertexWhiteLogo from '@/assets/img/Vertex_white.svg';


export default {
  name: 'ContractPage',

  components: {
    LoadingPage,
    ShareContractModal,
    AskQuestionModal,
  },

  props: {
    unique_key: {
      type: String,
      required: true,
    },
    access_key: {
      type: String,
      required: true
    },
  },

  data() {
    return {
      showAskQuestionModal: false,
      showShareModal: false,
      showProceedWizard: false, 
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      isExpired: false,
      isDeleted: false,
      showMeetModal: false,
      showRevisionsModal: false,
      showMeetingBooking: false,
      isEditSidebarCollapsed: false,
      editSidebarTooltip: '',
      activeChatRevisionId: null,
      chatComments: [],
      newCommentText: '',
      isSubmittingComment: false,
      showSampleContractsModal: false,
      isSidebarCollapsed: false,
      isMobileDrawerOpen: false,
      fullLogo: VertexWhiteLogo,
      collapsedLogo: VLogo,
      contract: {
        Lead: { organisation_name: "", abn: "", first_name: "", last_name: "", is_charity: false, email: "" },
        contract_section_content: { cover_page_content: "", product_service_content: "", image_grid_content: "", testimonials_content: "", content_conflict: "", content_insurance: "", content_whs_responsibilities: "", content_terms_conditions: "" },
        contract_title: "",
        contract_owner: "",
        expiry_date: "",
        contract_items: [],
        contract_version: "1.0",
        is_sent: false,
        show_discount_calculation: false,
        contract_reissue: true,
        is_accepted: false,
        accepted_at: "",
        accepted_by_email: "",
        xodo_status: "",
        viewer_has_signed: false,
        viewer_can_sign: false,
        shared_users: [],
        option_index_selected: null,
        contract_acceptance: null,
      },
      ids: [],
      contract_items: [],
      filteredContractItems: [],
      isLoading: false,
      isModalLoading: false,
      keyType: null,
      IsHidden: true,
      sampleContracts: [],
      filteredSampleContracts: [],
      sampleContractSearch: '',
      vertex_bdm: '',
      vertex_bdm_email: '',
      vertex_bdm_mobile: '',
      vertex_bdm_calendar_link: '',
      contract_settings: null,
      // ---- Authority to sign ----
      authorityConfirmed: false,
      showAuthorityWarning: false,
      isConfirmingAuthority: false,
      isCheckingStatus: false, // Guard against simultaneous background polls
      isResendingHandoff: false, // Loading spinner state for manual handoff resends
      statusPollInterval: null,
      // ---- JSON section management ----
      localContractSections: [],  // mutable local copy of parsed sections for reordering
      dragSourceIdx: null,
      // ---- Section action panel ----
      sectionActionPanel: {
        show: false,
        section: null,   // the ContractSectionBlock being acted on
        action: '',      // 'comment' | 'revision' | 'delete'
        text: '',
        isSubmitting: false,
        submitted: false,
      },
      pendingSectionRevisions: [], // [{section_name, action}] loaded from API
      revisionUnreadCounts: {},
      isEditModeEnabled: false,
      reRenderKey: 0,
      toolbar: {
        show: false,
        x: 0,
        y: 0,
        selectedText: '',
        sectionType: ''
      },
      savedRange: null,
      selectionTimeout: null,
      selectedSectionNameForToolbar: null,
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      pendingDeleteRevisionId: null,
    };
  },

   async created() {
     this.isLoading = true;
     try {
       await this.init();
     } finally {
       this.isLoading = false;
     }
   },

  mounted() {
    document.addEventListener('mouseup', this.handleSelection);
    document.addEventListener('touchend', this.handleSelection);
    document.addEventListener('selectionchange', this.handleSelection);
    document.addEventListener('mousedown', this.handleMousedown);
    document.addEventListener('touchstart', this.handleMousedown);
    
    // Background poll for signature status every 20 seconds
    this.statusPollInterval = setInterval(() => {
      if (this.contract && this.contract.xodo_document_id && !this.contract.is_accepted && !this.isLoading) {
        this.checkSigningStatus(true); // Pass true for silent mode
      }
    }, 20000);
  },

  beforeUnmount() {
    document.removeEventListener('mouseup', this.handleSelection);
    document.removeEventListener('touchend', this.handleSelection);
    document.removeEventListener('selectionchange', this.handleSelection);
    document.removeEventListener('mousedown', this.handleMousedown);
    document.removeEventListener('touchstart', this.handleMousedown);
    if (this.selectionTimeout) {
      clearTimeout(this.selectionTimeout);
    }
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
    }
  },

  computed: {
    totalUnreadCount() {
      return Object.values(this.revisionUnreadCounts).reduce((sum, n) => sum + n, 0);
    },
    isAwaitingSignatures() {
      // Sequential contract status logic (matches Dashboard)
      const signers = this.contract.contract_acceptance?.signers || [];
      const isSequenced = signers.some(s => s.sequence > 1);
      const allToSign = this.contract.contract_acceptance?.all_to_sign;
      return allToSign && isSequenced && signers.length > 0;
    },
    activeUnsignedSigner() {
      if (!this.contract || !this.contract.contract_acceptance || !this.contract.contract_acceptance.signers) return null;
      const signers = this.contract.contract_acceptance.signers;
      let active = null;
      let minSeq = 999;
      for (const s of signers) {
        if (!s.has_signed && s.sequence < minSeq) {
          minSeq = s.sequence;
          active = s;
        }
      }
      return active;
    },
    activeRevisions() {
      return this.pendingSectionRevisions.filter(r => !r.is_resolved && !r.is_ignored);
    },
    displayOrganisationName() {
      // Prefer the client-submitted organisation name from contract acceptance,
      // then fall back to the lead's auth org name, then lead org name.
      const acceptance = this.contract.contract_acceptance;
      if (acceptance && acceptance.organisation_name && acceptance.organisation_name.trim()) {
        return acceptance.organisation_name.trim();
      }
      return this.contract.Lead.auth_organisation_name || this.contract.Lead.organisation_name || 'No Organisation Name';
    },
    businessNumberShortLabel() {
      const code = this.contract?.Lead?.country || 'AU';
      return getBusinessNumberShortLabel(code);
    },
    displayAbn() {
      const acceptance = this.contract.contract_acceptance;
      if (acceptance && (acceptance.business_number || acceptance.abn) && (acceptance.business_number || acceptance.abn).trim()) {
        return (acceptance.business_number || acceptance.abn).trim();
      }
      return this.contract.Lead.business_number || this.contract.Lead.abn || `No Registered ${this.businessNumberShortLabel}`;
    },
    // --- NEW: Computes status for the right sidebar indicator ---
    currentContractStatus() {
      // 1. Check if Accepted
      if (this.contract.is_accepted) {
        // Check if partially accepted (relies on existing logic)
        if (this.canAcceptAdditionalItems) {
          return 'partially_accepted';
        }
        return 'accepted';
      }

      if (this.contract.contract_status === 8) {
          return 'declined';
      }

      // 2. Check if Expired (ignoring internal keys)
      if (this.isContractExpired) {
        return 'expired';
      }

      // 3. Default to Active/Pending
      return 'pending';
    },

    canAcceptAdditionalItems() {
    // 1. First, perform guard checks to exit early if conditions aren't met.
    if (
      !this.contract.is_accepted ||                 // The contract must be accepted.
      !this.contract.option_index_selected ||       // We must know which option was selected.
      !this.contract.contract_items ||              // The contract must have items.
      this.contract.contract_items.length === 0 || 
      this.isContractExpired
    ) {
      return false;
    }

    // 2. Filter the master list to get ONLY the items from the selected option.
    const itemsInSelectedOption = this.contract.contract_items.filter(
      item => item.option_index === this.contract.option_index_selected
    );

    // If there are no items in this option for some reason, hide the button.
    if (itemsInSelectedOption.length === 0) {
        return false;
    }

    // 3. Now, check for a mix of accepted/unaccepted statuses *within that specific option*.
    const hasUnaccepted = itemsInSelectedOption.some(item => !item.added_to_contract);
    const hasAccepted = itemsInSelectedOption.some(item => item.added_to_contract);
    
    // 4. The button should only show if there's at least one unaccepted AND one accepted item in the chosen option.
    return hasUnaccepted && hasAccepted;
  },
    visibleSharedUsers() {
      if (!this.contract.shared_users) {
        return [];
      }
      return this.contract.shared_users.filter(user => !user.is_access_revoked);
    },
    acceptedContractItems() {
        if (!this.contract.is_accepted) {
            return [];
        }
        // Filter all contract items to get only the ones added to the contract
        const acceptedItems = this.contract.contract_items.filter(item => item.added_to_contract);
        
        // Group them into a single "option" structure for easy rendering in the table
        if (acceptedItems.length > 0) {
            return [{
                option_index: this.contract.option_index_selected,
                productList: acceptedItems
            }];
        }
        return [];
    },
    mainOptionProductList() {
      const mainOption = this.filteredContractItems.find(pi => pi.option_index === 1);
      return mainOption ? mainOption.productList : [];
    },
    executionDateDisplay() {
      // Use accepted_at if available; fall back to created_at for legacy contracts
      const acceptedAt = this.contract.accepted_at;
      if (acceptedAt && !acceptedAt.startsWith('0001')) {
        return this.formatUtcToTimeZone(acceptedAt);
      }
      return this.formatUtcToTimeZone(this.contract.created_at);
    },
    hasExecutionPageSection() {
      if (!this.orderedContractSections || this.orderedContractSections.length === 0) return false;
      return this.orderedContractSections.some(s => s.type === 'ExecutionPage');
    },
    declinedDateDisplay() {
      const declinedAt = this.contract.declined_at;
      if (declinedAt && !declinedAt.startsWith('0001')) {
        return this.formatUtcToTimeZone(declinedAt);
      }
      return this.formatUtcToTimeZone(this.contract.created_at);
    },
    isContractExpired() {
      if (this.keyType === 'internal') {
        return false;
      }
      if (this.contract.contract_status === 0) {
        return false;
      }
      if (this.contract.contract_status === 9) {
        return true;
      }
      if (!this.contract.expiry_date || this.DaysToAcceptCounter(this.contract.expiry_date) < -1000) {
        return false;
      }
      return this.isExpired || this.DaysToAcceptCounter(this.contract.expiry_date) <= 0;
    },
    canShareContract() {
      if (this.isContractExpired || this.isDeleted) {
        return false;
      }
      return this.contract.key_type !== 'internal' && this.contract.share_type === 'signer';
    },
    canAcceptContract() {
      return this.contract.key_type !== 'internal' && this.contract.share_type !== 'viewer' && this.contract.viewer_can_sign;
    },
    isContractInReview() {
      return this.contract.contract_status === 3 || this.pendingSectionRevisions.some(r => !r.is_resolved && !r.is_ignored);
    },
    executedSigners() {
      const signers = [];
      if (this.contract.contract_acceptance && this.contract.contract_acceptance.signers) {
        this.contract.contract_acceptance.signers.forEach(s => {
          if (s.has_signed || this.contract.is_accepted) {
            const formattedName = [s.first_name || s.firstName, s.last_name || s.lastName].filter(Boolean).join(' ') || s.name || s.email;
            signers.push({
              first_name: s.first_name || s.firstName,
              last_name: s.last_name || s.lastName,
              firstName: s.first_name || s.firstName,
              lastName: s.last_name || s.lastName,
              name: formattedName,
              email: s.email
            });
          }
        });
      }
      if (signers.length === 0 && this.contract.is_accepted) {
        if (this.contract.accepted_by_email) {
          const clientFirst = this.contract.Lead?.first_name || '';
          const clientLast = this.contract.Lead?.last_name || '';
          const clientName = [clientFirst, clientLast].filter(Boolean).join(' ');
          signers.push({
            first_name: clientFirst,
            last_name: clientLast,
            firstName: clientFirst,
            lastName: clientLast,
            name: clientName || this.contract.accepted_by_email,
            email: this.contract.accepted_by_email
          });
        } else if (this.contract.Lead) {
          const clientFirst = this.contract.Lead?.first_name || '';
          const clientLast = this.contract.Lead?.last_name || '';
          const clientName = [clientFirst, clientLast].filter(Boolean).join(' ');
          if (clientName || this.contract.Lead.email) {
            signers.push({
              first_name: clientFirst,
              last_name: clientLast,
              firstName: clientFirst,
              lastName: clientLast,
              name: clientName || this.contract.Lead.email,
              email: this.contract.Lead.email || ''
            });
          }
        }
      }
      return signers;
    },

    // ---- JSON Contract Sections ----
    parsedContractSections() {
      const raw = this.contract?.contract_section_content?.contract_sections_json;
      if (!raw || raw === '') return [];
      try {
        let sections = JSON.parse(raw);

        // Clean up intermediate markers that are only used by the PDF pipeline
        sections = sections.map(s => {
          if (!s.content) return s;
          let content = s.content;

          if (this.contract.is_accepted) {
            let acceptedAtVal = this.contract.accepted_at;
            if (!acceptedAtVal || acceptedAtVal.startsWith('0001')) {
              acceptedAtVal = this.contract.created_at;
            }
            const execDate = new Date(acceptedAtVal).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: '2-digit'
            });

            const executedPlaceholder = (title, signerInfo, date) =>
              `<div style="background-color: #f4faf6; padding: 25px; border: 2px solid #28a745; border-radius: 4px; margin: 10px 0; font-family: sans-serif; text-align: left;">` +
              `<h3 style="color: #196f3d; margin: 0 0 10px 0; font-family: sans-serif; font-size: 15px; font-weight: bold; display: flex; align-items: center; gap: 8px;">` +
              `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>` +
              `${title}</h3>` +
              `<div style="font-size: 13px; color: #333; line-height: 1.5; font-family: sans-serif;">` +
              `<p style="margin: 3px 0;"><strong>Signed by:</strong> ${signerInfo}</p>` +
              `<p style="margin: 3px 0;"><strong>Date:</strong> ${date}</p>` +
              `<p style="margin: 8px 0 0 0; color: #666; font-size: 11px; font-style: italic;">Verification: Fully signed via EverSign digital signature protocol. Full audit logs are available in the downloaded contract PDF.</p>` +
              `</div>` +
              `</div>`;

            // Prepare client signer info string
            let clientSignerDetails = "";
            const activeSigners = this.executedSigners;
            if (activeSigners && activeSigners.length > 0) {
              clientSignerDetails = activeSigners.map(as => as.email ? `${as.name} (${as.email})` : as.name).join(', ');
            } else {
              clientSignerDetails = this.contract.accepted_by_email || "Authorised Signatory";
            }

            // 1. Internal signature replacement
            const internalSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*(?:\{\{XODO_INTERNAL_SIGNATURE\}\}|\[sig\|req\|signer1[^\]]*\])\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              internalSigRegex,
              executedPlaceholder('Vertex Technologies Pty Ltd Signature', this.contract_settings?.signatory_name || this.vertex_bdm || 'Martin Boyd', execDate)
            );

            // 2. External / Client signatures replacement
            const externalSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*(?:\{\{XODO_EXTERNAL_SIGNATURE\}\}|\[sig\|req\|signer[2-9]\d*[^\]]*\])\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              externalSigRegex,
              executedPlaceholder('Client Signature', clientSignerDetails, execDate)
            );

            // Fallback for any remaining [sig|req|signer...] tags
            const fallbackSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*\[sig\|req\|signer\d+[^\]]*\]\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              fallbackSigRegex,
              executedPlaceholder('Client Signature', clientSignerDetails, execDate)
            );

            // 3. Date replacement
            content = content.replace(
              /<span[^>]*style="[^"]*color:\s*white[^"]*"[^>]*>\s*(?:\[Date of Execution\]|\[date\|req\|signer\d+\])\s*<\/span>|<span[^>]*>\s*\[date\|req\|signer\d+\]\s*<\/span>|\[Date of Execution\]|\[date\|req\|signer\d+\]/gi,
              `<strong>${execDate}</strong>`
            );

            // 4. Cleanup text/name tags
            content = content.replace(
              /<span[^>]*style="[^"]*color:\s*white[^"]*"[^>]*>\s*\[(?:text|name)\|req\|signer\d+[^\]]*\]\s*<\/span>|\[(?:text|name)\|req\|signer\d+[^\]]*\]/gi,
              ''
            );
          } else {
            const automatedSignerPlaceholder = (signerName, title) =>
              `<div class="contract-sig-placeholder-card" style="border: 1px dashed #bbb; border-radius: 4px; padding: 15px 18px; background: #fafafa; margin: 12px 0; font-family: sans-serif; font-size: 13px; text-align: left; color: #444; box-shadow: inset 0 1px 3px rgba(0,0,0,0.02); box-sizing: border-box; width: 100%;">` +
              `<div style="margin: 0 0 12px 0; font-weight: bold; color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;">` +
              `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #f39c12; flex-shrink: 0;"></span>` +
              `<span>Pending Digital Signature [${title}]</span></div>` +
              `<div style="margin-bottom: 12px; display: flex; align-items: flex-end; gap: 8px;">` +
              `<span style="min-width: 75px; width: 75px; font-weight: 500; color: #555; flex-shrink: 0;">Signature:</span>` +
              `<div style="border-bottom: 1px dashed #bbb; flex: 1 1 auto; min-height: 22px; font-size: 11px; font-style: italic; color: #888; display: flex; align-items: center; padding: 0 6px; box-sizing: border-box; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">` +
              `[Digitally Signed via EverSign]` +
              `</div>` +
              `</div>` +
              `<div style="margin-bottom: 12px; display: flex; align-items: flex-end; gap: 8px;">` +
              `<span style="min-width: 75px; width: 75px; font-weight: 500; color: #555; flex-shrink: 0;">Name:</span>` +
              `<div style="border-bottom: 1px dashed #bbb; flex: 1 1 auto; min-height: 22px; font-weight: 600; color: #333; display: flex; align-items: center; padding: 0 6px; box-sizing: border-box; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">` +
              `${signerName}` +
              `</div>` +
              `</div>` +
              `<div style="display: flex; align-items: flex-end; gap: 8px;">` +
              `<span style="min-width: 75px; width: 75px; font-weight: 500; color: #555; flex-shrink: 0;">Date:</span>` +
              `<div style="border-bottom: 1px dashed #bbb; flex: 1 1 auto; min-height: 22px; font-size: 11px; font-style: italic; color: #888; display: flex; align-items: center; padding: 0 6px; box-sizing: border-box; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">` +
              `[To be dated upon execution]` +
              `</div>` +
              `</div>` +
              `</div>`;

            const vertexSigner = this.contract_settings?.signatory_name || this.vertex_bdm || 'Martin Boyd';

            let clientSignersList = [];
            if (this.contract.shared_users) {
              clientSignersList = this.contract.shared_users
                .filter(user => user.share_type === 'signer' && !user.is_access_revoked)
                .map(user => user.signer_name || user.email);
            }
            if (clientSignersList.length === 0) {
              if (this.contract.Lead) {
                const clientName = `${this.contract.Lead.first_name || ''} ${this.contract.Lead.last_name || ''}`.trim();
                clientSignersList.push(clientName || this.contract.Lead.email || 'Authorised Signatory');
              } else {
                clientSignersList.push('Authorised Signatory');
              }
            }

            // 1. Internal signature replacement
            const internalSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*(?:\{\{XODO_INTERNAL_SIGNATURE\}\}|\[sig\|req\|signer1[^\]]*\])\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              internalSigRegex,
              automatedSignerPlaceholder(vertexSigner, 'Automated Vertex Signature')
            );

            // 2. External / Client signatures replacement
            const externalSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*(?:\{\{XODO_EXTERNAL_SIGNATURE\}\}|\[sig\|req\|signer[2-9]\d*[^\]]*\])\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              externalSigRegex,
              automatedSignerPlaceholder(clientSignersList.join(', '), 'Automated Client Signature')
            );

            // Fallback for any remaining [sig|req|signer...] tags
            const fallbackSigRegex = /(?:<p[^>]*>\s*(?:&nbsp;|\s)*)?(?:<div[^>]*style="[^"]*height:\s*65px[^"]*"[^>]*>\s*)?(?:<mark[^>]*>)?(?:<(?:b|strong)>)?\s*\[sig\|req\|signer\d+[^\]]*\]\s*(?:<\/(?:b|strong)>)?(?:<\/mark>)?(?:\s*<\/div>)?(?:\s*<\/p>)?/gi;
            content = content.replace(
              fallbackSigRegex,
              automatedSignerPlaceholder(clientSignersList.join(', '), 'Automated Client Signature')
            );

            // 3. Date replacement
            const prettyExecutionDatePlaceholder =
              `<span style="display: inline-flex; align-items: center; gap: 6px; padding: 2px 8px; background-color: #fdf2e9; border: 1px dashed #e67e22; border-radius: 4px; color: #d35400; font-family: sans-serif; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle; -webkit-print-color-adjust: exact;">` +
              `<span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background-color: #e67e22;"></span>` +
              `To be dated upon execution` +
              `</span>`;
            content = content.replace(
              /<span[^>]*style="[^"]*color:\s*white[^"]*"[^>]*>\s*(?:\[Date of Execution\]|\[date\|req\|signer\d+\])\s*<\/span>|<span[^>]*>\s*\[date\|req\|signer\d+\]\s*<\/span>|\[Date of Execution\]|\[date\|req\|signer\d+\]/gi,
              prettyExecutionDatePlaceholder
            );

            // 4. Cleanup text/name tags
            content = content.replace(
              /<span[^>]*style="[^"]*color:\s*white[^"]*"[^>]*>\s*\[(?:text|name)\|req\|signer\d+[^\]]*\]\s*<\/span>|\[(?:text|name)\|req\|signer\d+[^\]]*\]/gi,
              ''
            );
          }
          content = content.replace(/\{\{XODO_SIGNATURE\}\}/g, '');

          return { ...s, content };
        });

        return sections;
      } catch { return []; }
    },
    orderedContractSections() {
      const base = (this.isDraftMode && this.localContractSections.length > 0)
        ? this.localContractSections
        : this.parsedContractSections;
      let sections = [...base].sort((a, b) => a.sequence - b.sequence);
      // Hide the contract "Execution" section on contracts which are pending any signatures
      if (!this.contract || !this.contract.is_accepted) {
        sections = sections.filter(s => s.type !== 'ExecutionPage');
      }
      return sections;
    },
    isDraftMode() {
      // Internal keys are preview-only — no dragging/reordering
      return false;
    },
    canEditSections() {
      // Only external (signer) keys with edit mode enabled can annotate
      return this.isEditModeEnabled && this.contract.key_type !== 'internal' && !this.isContractExpired && this.contract.share_type !== 'viewer';
    },
    canClientSuggestRevision() {
      // Clients (non-internal, non-viewer, not expired, not executed/declined, not already signed)
      // can suggest revisions even if not in edit mode.
      return this.contract.key_type !== 'internal' && 
             !this.isContractExpired && 
             this.contract.share_type !== 'viewer' &&
             !this.contract.is_accepted &&
             this.currentContractStatus !== 'declined' &&
             !this.contract.viewer_has_signed;
    },
  },


  methods: {
    formatSignDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    },
    async resendCurrentHandoff() {
      if (this.isResendingHandoff) return;
      this.isResendingHandoff = true;
      try {
        const res = await this.$axios_internal.post(`/public/contracts/${this.unique_key}/${this.access_key}/resend-handoff`);
        if (res.data && res.data.success) {
          this.$toast.success(res.data.message || 'Handoff email resent successfully!');
        } else {
          this.$toast.error(res.data?.message || 'Failed to resend email.');
        }
      } catch (err) {
        this.$toast.error(err.response?.data?.message || 'An error occurred while resending the email.');
      } finally {
        this.isResendingHandoff = false;
      }
    },
    toggleEditMode() {
      this.isEditModeEnabled = !this.isEditModeEnabled;
      if (!this.isEditModeEnabled) {
        this.exitEditMode();
      }
    },
    exitEditMode() {
      this.isEditModeEnabled = false;
      this.toolbar.show = false;
      this.toolbar.selectedText = '';
      this.savedRange = null;
      const sel = window.getSelection();
      if (sel) sel.removeAllRanges();
    },
    handleMousedown(event) {
      if (this.toolbar.show && !event.target.closest('.pdf-toolbar')) {
        // Don't auto-hide if interacting with toolbar buttons
      }
    },
    
    handleSelection() {
      if (!this.isEditModeEnabled) {
        this.toolbar.show = false;
        this.toolbar.selectedText = '';
        this.savedRange = null;
        return;
      }

      if (this.selectionTimeout) {
        clearTimeout(this.selectionTimeout);
      }

      this.selectionTimeout = setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
          const activeEl = document.activeElement;
          if (activeEl && activeEl.closest('.pdf-toolbar')) {
            return;
          }
          this.toolbar.selectedText = '';
          this.savedRange = null;
          return;
        }

        const text = selection.toString().trim();
        if (text.length > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          const rootElement = container.nodeType === 3 ? container.parentNode : container;
          
          // Only show toolbar if selecting inside the main content area
          const contentArea = document.querySelector('.contract-sections-wrapper') || document.querySelector('.contract-details');
          if (contentArea && contentArea.contains(rootElement)) {
            const rect = range.getBoundingClientRect();
            
            // Try to figure out what section they selected text in (for better backend tracking)
            let sectionType = 'General Content';
            const block = rootElement.closest('.contract-section-block');
            if (block && block.dataset.sectionType) {
              sectionType = block.dataset.sectionType;
            }
            
            this.savedRange = range.cloneRange();
            this.toolbar = {
              show: true,
              x: rect.left + (rect.width / 2) - 60,
              y: rect.top + window.scrollY - 50,
              selectedText: text,
              sectionType: sectionType
            };
            return;
          }
        }
        this.toolbar.selectedText = '';
        this.savedRange = null;
      }, 50);
    },

    isRestrictedSection(sectionType) {
      return sectionType === 'ScheduleTable' || sectionType === 'ExecutionPage';
    },

    async submitSectionActionDirect(section, action, text) {
      this.sectionActionPanel.isSubmitting = true;
      try {
        const res = await this.$axios_internal.post(
          `/public/contracts/${this.unique_key}/${this.access_key}/revision`,
          {
            section_name: section.type,
            action,
            content: text,
            submitted_by: this.contract.viewer_signer_email || this.contract.Lead?.email || '',
          },
        );
        this.pendingSectionRevisions.push(res.data.revision);
        const actionLabel =
          action === 'comment' ? 'Comment' :
          action === 'revision' ? 'Revision' :
          action === 'strikethrough' ? 'Strike-through' :
          action === 'strikethrough_comment' ? 'Strike-through with comment' :
          'Deletion request';
        this.$toast.success(actionLabel + ' submitted successfully.');
        this.triggerReapplyAnnotations();
      } catch (e) {
        this.$toast.error('Failed to submit. Please try again.');
      } finally {
        this.sectionActionPanel.isSubmitting = false;
      }
    },

    async toggleChat(revId) {
       if (this.activeChatRevisionId === revId) {
          this.activeChatRevisionId = null;
          this.chatComments = [];
          return;
       }
       this.activeChatRevisionId = revId;
       this.chatComments = [];

       // Mark the other party's comments as read (Client opening → mark BDM comments as read)
       try { await this.$axios_internal.patch(`/public/contracts/${this.unique_key}/${this.access_key}/revisions/${revId}/comments/read`); } catch(e) { /* silent */ }
       delete this.revisionUnreadCounts[revId];

       try {
         const res = await this.$axios_internal.get(`/public/contracts/${this.unique_key}/${this.access_key}/revisions/${revId}/comments`);
         if(res.data && res.data.comments) {
            this.chatComments = res.data.comments;
         }
       } catch (e) {
          this.$toast.error('Failed to load chat thread.');
       }
    },

    async postComment(revId) {
       if(!this.newCommentText.trim()) return;
       this.isSubmittingComment = true;
       try {
         const res = await this.$axios_internal.post(`/public/contracts/${this.unique_key}/${this.access_key}/revisions/${revId}/comments`, {
            comment_text: this.newCommentText
         });
         if(res.data && res.data.comment) {
           this.chatComments.push(res.data.comment);
           this.newCommentText = '';
           this.$toast.success('Reply sent.');
         }
       } catch (e) {
         this.$toast.error('Failed to post comment.');
       } finally {
         this.isSubmittingComment = false;
       }
    },

    async deleteClientRevision(revisionId) {
       this.pendingDeleteRevisionId = revisionId;
       this.confirmModalTitle = 'Delete Revision';
       this.confirmModalMessage = 'Are you sure you want to delete this revision?';
       this.showConfirmModal = true;
    },

    async onConfirmAction() {
       const revisionId = this.pendingDeleteRevisionId;
       this.pendingDeleteRevisionId = null;
       if (!revisionId) return;
       try {
          await this.$axios_internal.post(`/public/contracts/${this.unique_key}/${this.access_key}/revisions/${revisionId}/delete`);
          this.$toast.success('Revision deleted successfully.');
          this.pendingSectionRevisions = this.pendingSectionRevisions.filter(r => r.id !== revisionId);
          if (this.activeRevisions.length === 0) {
             this.showRevisionsModal = false;
          }
          
          this.triggerReapplyAnnotations();
       } catch (e) {
          this.$toast.error('Failed to delete revision.');
       }
    },

    reApplyVisualAnnotations(revisions) {
      if (!revisions || revisions.length === 0) return;
      const scrollBefore = window.scrollY;
      
      // Force window focus to ensure native window.find Selection engine executes successfully during headless initialization loops
      if (typeof window.focus === 'function') {
          window.focus();
      }
      
      const sel = window.getSelection();
      sel.removeAllRanges();
      const initialRange = document.createRange();
      initialRange.selectNodeContents(document.body);
      initialRange.collapse(true);
      sel.addRange(initialRange);
      
      revisions.forEach(rev => {
         if (rev.is_resolved || rev.is_ignored) return;
         if (!rev.content) return;
         
         const action = rev.action;
         let cleanText = null;
         let userComment = "";

         if (rev.content.startsWith(">>>")) {
             let textStart = rev.content.indexOf(':\n"');
             if (textStart === -1) textStart = rev.content.indexOf(':\r\n"');
             
             if (textStart !== -1) {
                 const isWindows = rev.content.substr(textStart, 4) === ':\r\n"';
                 const offset = isWindows ? 4 : 3;
                 const contentBody = rev.content.slice(textStart + offset); 
                 
                 let lastQuoteNewline = contentBody.lastIndexOf('"\n\n');
                 let commentOffset = 3;
                 if (lastQuoteNewline === -1) {
                     lastQuoteNewline = contentBody.lastIndexOf('"\r\n\r\n');
                     commentOffset = 5;
                 }
                 
                 if (lastQuoteNewline !== -1) {
                      cleanText = contentBody.substring(0, lastQuoteNewline);
                      userComment = contentBody.substring(lastQuoteNewline + commentOffset).trim();
                 } else if (contentBody.endsWith('"')) {
                      cleanText = contentBody.substring(0, contentBody.length - 1);
                 } else {
                      cleanText = contentBody;
                 }
             }
         }

         if (!cleanText) {
             // Fallback for raw DB anomalies without wrappers
             cleanText = rev.content;
         }
         
         if (cleanText) {
            const cssClass = action === 'strikethrough' ? 'strike-redline'
                           : action === 'strikethrough_comment' ? 'strike-comment-highlight' 
                           : 'comment-highlight';

             let container = document.querySelector(`[data-section-type="${rev.section_name}"]`);
             if (!container) {
                 container = document.querySelector('.contract-sections-wrapper');
             }
             if (!container) return;

            // Helper: normalize all quote variants to straight quotes for matching
            const normalizeQuotes = (str) => str
                .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
                .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
                .replace(/\u00AB|\u00BB/g, '"');

            const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
            const nodes = [];
            let unifiedString = "";
            
            let node;
            while ((node = walker.nextNode())) {
                const text = node.nodeValue;
                nodes.push({ node, start: unifiedString.length, end: unifiedString.length + text.length });
                unifiedString += text;
            }
            
            // Normalize both strings: collapse whitespace + normalize quotes
            const normalizedUnified = normalizeQuotes(unifiedString).replace(/\s+/g, ' ');
            const normalizedSearch = normalizeQuotes(cleanText.trim()).replace(/\s+/g, ' ');
            
            if (normalizedSearch.length === 0) return;
            
            // Simple substring search on normalized strings
            const matchPos = normalizedUnified.toLowerCase().indexOf(normalizedSearch.toLowerCase());
            
            if (matchPos !== -1) {
                // Map the normalized position back to the original unifiedString position.
                // Build a mapping from normalized index → original index.
                const origToNorm = [];
                let prevWasSpace = false;
                for (let k = 0; k < unifiedString.length; k++) {
                    const ch = unifiedString[k];
                    const isSpace = /\s/.test(ch);
                    if (isSpace && prevWasSpace) {
                        // collapsed whitespace — skip
                        continue;
                    }
                    origToNorm.push(k);
                    prevWasSpace = isSpace;
                }
                
                const origStart = origToNorm[matchPos] !== undefined ? origToNorm[matchPos] : matchPos;
                const endNorm = matchPos + normalizedSearch.length - 1;
                const origEnd = (origToNorm[endNorm] !== undefined ? origToNorm[endNorm] : endNorm) + 1;
                
                const nodesToWrap = [];
                for (let idx = 0; idx < nodes.length; idx++) {
                    const n = nodes[idx];
                    if (n.end > origStart && n.start < origEnd) {
                         const localStart = Math.max(0, origStart - n.start);
                         const localEnd = Math.min(n.node.nodeValue.length, origEnd - n.start);
                         if (localStart < localEnd) {
                             nodesToWrap.push({ node: n.node, start: localStart, end: localEnd });
                         }
                    }
                }
                
                nodesToWrap.reverse().forEach(nw => {
                     try {
                         // Skip text nodes inside table-structural elements (TABLE, THEAD, TBODY, TFOOT, TR, COLGROUP)
                         // Inserting <span> there creates invalid HTML and breaks table layout
                         const parentTag = nw.node.parentNode && nw.node.parentNode.nodeName;
                         if (['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'COLGROUP'].includes(parentTag)) {
                             return;
                         }
                         const range = document.createRange();
                         range.setStart(nw.node, nw.start);
                         range.setEnd(nw.node, nw.end);
                         const span = document.createElement('span');
                         span.className = cssClass;
                         if (userComment) span.setAttribute('data-comment', userComment);
                         range.surroundContents(span);
                     } catch(err) {
                         // Ignore wrap exception
                     }
                });
            }
         }
      });
      
      window.scrollTo({ top: scrollBefore, behavior: 'instant' });
      window.getSelection().removeAllRanges();
    },

    wrapSelection(cssClass, dataAttribute = null, dataValue = null, customRange = null) {
      let range = customRange || this.savedRange;
      if (!range) {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount || selection.isCollapsed) return;
        range = selection.getRangeAt(0);
      }

      if (!range) return;

      const rootNode = range.commonAncestorContainer.nodeType === 3 
        ? range.commonAncestorContainer.parentNode 
        : range.commonAncestorContainer;

      const walker = document.createTreeWalker(
        rootNode,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
          }
        },
        false
      );

      const nodesToWrap = [];
      let currentNode;
      while ((currentNode = walker.nextNode())) {
        nodesToWrap.push(currentNode);
      }

      if (nodesToWrap.length === 0) {
        if (range.commonAncestorContainer.nodeType === 3) {
          nodesToWrap.push(range.commonAncestorContainer);
        } else {
          return;
        }
      }

      const startNode = nodesToWrap[0];
      const endNode = nodesToWrap[nodesToWrap.length - 1];

      if (range.endContainer === endNode && endNode.nodeType === Node.TEXT_NODE) {
        endNode.splitText(range.endOffset);
      }

      if (range.startContainer === startNode && startNode.nodeType === Node.TEXT_NODE) {
        const rightPart = startNode.splitText(range.startOffset);
        nodesToWrap[0] = rightPart; 
      }

      nodesToWrap.forEach(textNode => {
        if (textNode.textContent.trim().length === 0) return; 

        const span = document.createElement('span');
        span.className = cssClass;
        
        if (dataAttribute && dataValue) {
          span.setAttribute(dataAttribute, dataValue);
        }

        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(span, textNode);
          span.appendChild(textNode);
        }
      });

      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
      }
    },

    openToolbarAction(actionType) {
      const selection = window.getSelection();
      let range = this.savedRange;
      if (!range && selection && selection.rangeCount > 0 && !selection.isCollapsed) {
        range = selection.getRangeAt(0);
      }

      if (!range) {
        this.$toast.error('Please highlight text to annotate first.');
        return;
      }
      
      let node = range.commonAncestorContainer;
      if (node.nodeType === 3) node = node.parentNode;

      // Block editing on Schedule and Execution sections
      const sectionBlock = node.closest && node.closest('.contract-section-block');
      if (sectionBlock) {
        const sType = sectionBlock.dataset.sectionType;
        if (sType === 'ScheduleTable' || sType === 'ExecutionPage') {
          this.$toast.error('This section cannot be edited.');
          this.toolbar.show = false;
          this.toolbar.selectedText = '';
          this.savedRange = null;
          if (selection) selection.removeAllRanges();
          return;
        }
      }
      
      const isInside = node.closest && node.closest('.strike-redline, .comment-highlight, .strike-comment-highlight');
      const fragment = range.cloneContents();
      const containsHighlight = fragment.querySelector && fragment.querySelector('.strike-redline, .comment-highlight, .strike-comment-highlight');
      
      if (isInside || containsHighlight) {
          this.$toast.error("This portion of text already has an active revision.");
          this.toolbar.show = false;
          this.toolbar.selectedText = '';
          this.savedRange = null;
          if (selection) selection.removeAllRanges();
          return;
      }

      this.toolbar.show = false;
      
      const selectedText = this.toolbar.selectedText || range.toString().trim();
      const sectionType = this.toolbar.sectionType || (sectionBlock ? sectionBlock.dataset.sectionType : 'General Content') || 'General Content';
      const fakeSection = { type: sectionType };
      let initialText = `>>> Selected text:\n"${selectedText}"\n\n`;
      
      if (actionType === 'strikethrough') {
          initialText = `>>> Strikethrough requested for text:\n"${selectedText}"`;
          this.submitSectionActionDirect(fakeSection, actionType, initialText);
          // Red strike instantly!
          this.wrapSelection('strike-redline', null, null, range);
          this.toolbar.selectedText = '';
          this.savedRange = null;
          return;
      } 
      
      let userComment = prompt(actionType === 'comment' ? 'Enter your comment for this section:' : 'Reason for striking this out:');
      if (userComment === null) return; // user cancelled

      // Append user comment
      initialText += userComment;

      this.submitSectionActionDirect(fakeSection, actionType, initialText);
      const cssClass = actionType === 'comment' ? 'comment-highlight' : 'strike-comment-highlight';
      this.wrapSelection(cssClass, 'data-comment', userComment, range);
      this.toolbar.selectedText = '';
      this.savedRange = null;
    },


    // ---- JSON Section drag-to-reorder ----
    onDragStart(event, idx) {
      this.dragSourceIdx = idx;
    },
    onDragOver(event, idx) {
      if (this.dragSourceIdx === null || this.dragSourceIdx === idx) return;
      const targetRect = event.target.getBoundingClientRect();
      const midpoint = targetRect.top + targetRect.height / 2;
      this.dragTargetIdx = idx;
      this.dragDropPosition = event.clientY > midpoint ? 'after' : 'before';
    },
    async onDrop(event, idx) {
      if (this.dragSourceIdx === null) return;
      
      let newSectionOrder = [...this.orderedContractSections];
      const [movedItem] = newSectionOrder.splice(this.dragSourceIdx, 1);
      
      let insertIdx = idx;
      if (this.dragSourceIdx < idx && this.dragDropPosition === 'before') insertIdx--;
      if (this.dragSourceIdx > idx && this.dragDropPosition === 'after') insertIdx++;
      
      newSectionOrder.splice(insertIdx, 0, movedItem);
      this.contractSections = newSectionOrder;
      
      // Cleanup drag state
      this.dragSourceIdx = null;
      this.dragTargetIdx = null;
      
      // Save
      await this.saveSectionOrder();
    },
    
    async saveSectionOrder() {
      const sequences = this.contractSections.map((sec, index) => ({
        section_name: sec.type,
        sequence: index + 1
      }));
      
      try {
        await this.$axios_internal.post(`/contracts/${this.contract.uuid}/sequence`, sequences);
      } catch (e) {
        // Could not persist order
      }
    },

    // ---- Section action panel ----
    openSectionAction(section, action) {
      if (!this.isEditModeEnabled) {
        this.isEditModeEnabled = true;
      }
      
      // Auto-submit instantly if no comment is required (inline strikethrough only)
      if (action === 'strikethrough') {
        let initialText = '';
        if (this.toolbar.selectedText && this.toolbar.sectionType === section.type) {
            initialText = `>>> Strikethrough requested for text:\n"${this.toolbar.selectedText}"\n\n`;
        } else {
            initialText = `>>> Strikethrough requested for entire section.`;
        }
        this.submitSectionActionDirect(section, action, initialText);
        if (this.toolbar.selectedText) {
           this.wrapSelection('strike-redline');
        }
        
        // reset toolbar since selection is handled
        this.toolbar.show = false;
        this.toolbar.selectedText = '';
        return;
      }
      
      // Only delete and revision flow here now (modal opens).
      // We pass empty string so the text area is cleanly empty.
      this.sectionActionPanel = {
        show: true,
        section,
        action,
        text: '',
        isSubmitting: false,
        submitted: false,
      };
      
      this.$nextTick(() => {
        if (this.$refs.actionTextarea) {
          const ta = Array.isArray(this.$refs.actionTextarea) ? this.$refs.actionTextarea[0] : this.$refs.actionTextarea;
          if (ta && typeof ta.focus === 'function') ta.focus();
        }
      });
    },
    closeSectionAction() {
      this.sectionActionPanel.show = false;
    },
    async submitSectionAction() {
      const { section, action, text } = this.sectionActionPanel;

      if (!text.trim()) {
        const errorType = action === 'delete' ? 'deletion reason' : 'revision notes';
        this.$toast.error(`Please enter your ${errorType}.`);
        return;
      }
      
      let finalContent = text;
      if (action === 'delete') {
         finalContent = `>>> Deletion requested for entire section.\n\n` + text;
      } else if (action === 'revision') {
         finalContent = `>>> Revision requested for entire section.\n\n` + text;
      }

      this.sectionActionPanel.isSubmitting = true;
      try {
        const res = await this.$axios_internal.post(
          `/public/contracts/${this.unique_key}/${this.access_key}/revision`,
          {
            section_name: section.type,
            action,
            content: finalContent,
            submitted_by: this.contract.viewer_signer_email || this.contract.Lead?.email || '',
          },
        );
        this.sectionActionPanel.submitted = true;
        this.pendingSectionRevisions.push(res.data.revision);
        const actionLabel =
          action === 'comment' ? 'Comment' :
          action === 'revision' ? 'Revision' :
          action === 'strikethrough' ? 'Strike-through' :
          action === 'strikethrough_comment' ? 'Strike-through with comment' :
          'Deletion request';
        this.$toast.success(actionLabel + ' submitted successfully.');
        this.triggerReapplyAnnotations();
        setTimeout(() => this.closeSectionAction(), 1800);
      } catch (e) {
        this.$toast.error('Failed to submit. Please try again.');
      } finally {
        this.sectionActionPanel.isSubmitting = false;
      }
    },

    sectionHasPendingRevision(sectionType) {
      return this.pendingSectionRevisions.some(r => r.section_name === sectionType && !r.is_resolved && !r.is_ignored);
    },

    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },

    toggleMobileDrawer() {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
    },
    closeMobileDrawer() {
      this.isMobileDrawerOpen = false;
    },

    openAskQuestionModal() {
      this.showAskQuestionModal = true;
    },
    closeAskQuestionModal() {
      this.showAskQuestionModal = false;
    },
    openMeetModal() {
      this.showMeetModal = true;
    },
    closeMeetModal() {
      this.showMeetModal = false;
    },

    async startXodoSigning() {
      if (!this.authorityConfirmed) {
        this.showAuthorityWarning = true;
        this.$nextTick(() => {
          const el = document.querySelector('.authority-warning');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      if (!this.contract || !this.contract.xodo_signing_url) {
        this.$toast.error('Signature link hasn\'t generated yet or is unavailable.');
        return;
      }

      // Persist authority confirmation to the backend before redirecting
      this.isConfirmingAuthority = true;
      try {
        const leadEmail = this.contract.Lead?.email || this.vertex_bdm_email || '';
        await this.$axios_internal.post(
          `/public/contracts/${this.unique_key}/${this.access_key}/confirm-authority`,
          { email: leadEmail }
        );
        // Confirmation saved — redirect to Xodo Sign
        window.location.href = this.contract.xodo_signing_url;
      } catch (e) {
        const serverMsg = e?.response?.data?.message || 'Failed to save authority confirmation. Please try again.';
        this.$toast.error(serverMsg);
        this.isConfirmingAuthority = false;
      }
    },

    // Polls the backend to check if the Xodo document has been signed.
    // This is the fallback for local dev (where webhooks can't reach localhost)
    // and a safety net in production if a webhook is missed.
    async checkSigningStatus(silent = false) {
      if (!this.contract || !this.contract.xodo_document_id || this.contract.is_accepted) return;
      if (this.isCheckingStatus) return; // Already in progress, skip this cycle

      this.isCheckingStatus = true;

      try {
        const previousStatus = this.contract.contract_status;
        const res = await this.$axios_internal.get(
          `/public/contracts/${this.unique_key}/${this.access_key}/check-signing-status`
        );
        if (res.data && (res.data.is_accepted || res.data.contract_status === 4 || res.data.signing_status === 'completed')) {
          if (!this.contract.is_accepted) {
            this.contract.is_accepted = true;
            this.contract.contract_status = res.data.contract_status || 4;
            this.$toast.success('Contract has been signed and executed!');
          }
          await this.getContract(silent);
        } else if (res.data && res.data.contract_status) {
          // Always refresh contract data to sync individual signer statuses (sidebar)
          this.contract.contract_status = res.data.contract_status;
          await this.getContract(silent);
          
          if ((res.data.signing_status === 'declined' || res.data.signing_status === 'cancelled') && previousStatus !== 8) {
            this.$toast.error('This contract has been declined.');
          } else if (res.data.signing_status === 'expired' && previousStatus !== 9) {
            this.$toast.error('This contract has expired.');
          }
        }
      } catch (e) {
        // Could not check signing status
      } finally {
        this.isCheckingStatus = false;
      }
    },

    openSampleContractsModal() {
      this.showSampleContractsModal = true;
      if (this.sampleContracts.length === 0) {
        this.fetchSampleContracts();
      }
    },
    closeSampleContractsModal() {
      this.showSampleContractsModal = false;
    },
    async fetchSampleContracts() {
      this.isModalLoading = true;
      try {
        const response = await this.$axios_internal.get('/samples/sample-contracts');
        this.sampleContracts = response.data.sample_contracts.map(contract => ({
          id: contract.id,
          name: contract.name,
          contractType: contract.contract_type || 'Sample Terms',
          version: contract.version || 'v1.0',
          full_link: contract.full_link,
        }));
        this.filterSampleContracts();
      } catch (error) {
        this.sampleContracts = [];
        this.filteredSampleContracts = [];
      } finally {
        this.isModalLoading = false;
      }
    },
    filterSampleContracts() {
      if (!this.sampleContractSearch) {
        this.filteredSampleContracts = this.sampleContracts;
        return;
      }
      const searchTerm = this.sampleContractSearch.toLowerCase();
      this.filteredSampleContracts = this.sampleContracts.filter(contract =>
        contract.name.toLowerCase().includes(searchTerm)
      );
    },
    viewContract(contract) {
      if (contract.full_link) {
        window.open(contract.full_link, '_blank');
      } else {
        this.$toast.info("This contract does not have a file to view.");
      }
    },

    toOptionName,
    async init() {
       await this.getContract();
       // Auto-check status on load (useful when redirected back from Xodo Sign)
       if (this.contract && this.contract.xodo_document_id && !this.contract.is_accepted) {
         // Perform a brief poll loop while the full-screen LoadingPage is visible
         for (let attempt = 0; attempt < 3; attempt++) {
           await this.checkSigningStatus(true);
           if (this.contract.is_accepted) break;
           if (attempt < 2) {
             await new Promise(resolve => setTimeout(resolve, 1200));
           }
         }
       }
     },

    formatUtcToTimeZone(utcDateString) {
      const date = new Date(utcDateString);
      const options = {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if(date.toString() === "Invalid Date") return "";
      return new Intl.DateTimeFormat('en-US', options).format(date);
    },

    DaysToAcceptCounter(utcDateString) {
      const expiryDate = new Date(utcDateString);
      const now = new Date();
      const startOfTodayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfExpiryDayLocal = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
      const differenceInMs = startOfExpiryDayLocal.getTime() - startOfTodayLocal.getTime();
      const millisecondsPerDay = 1000 * 60 * 60 * 24;
      let daysDifference = differenceInMs / millisecondsPerDay;
      return Math.round(daysDifference);
    },

    calculateOptionTotalPrice(productList) {
      return productList.reduce((total, product) => {
        if (product.is_heading || product.IsHeading) return total;
        const productTotal = ((parseFloat(product.price) || 0) - (parseFloat(product.discount) || 0)) * (parseInt(product.quantity) || 0);
        return total + productTotal;
      }, 0) * 1.1;
    },

    calculateOptionSubTotalPrice(productList) {
      return productList.reduce((total, product) => {
        if (product.is_heading || product.IsHeading) return total;
        const productTotal = ((parseFloat(product.price) || 0) - (parseFloat(product.discount) || 0)) * (parseInt(product.quantity) || 0);
        return total + productTotal;
      }, 0);
    },

    calculateOptionGST(productList) {
      const subtotal = this.calculateOptionSubTotalPrice(productList);
      return subtotal * 0.1;
    },

    formatCurrency(value) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) {
        return '$0.00';
      }
      return `$${numericValue.toFixed(2)}`;
    },

    calculateTotal(item) {
      if (item.is_heading || item.IsHeading) return 0;
      return ((item.price || 0) - (item.discount || 0)) * (item.quantity || 0);
    },


    filterContractItems(contract_items) {
      if (!isEmpty(contract_items)) {
        this.ids = [];

        this.filteredContractItems = [];
        const groupedItems = {};
        contract_items.forEach((p) => {
          if (!groupedItems[p.option_index]) {
            groupedItems[p.option_index] = { productList: [], option_index: p.option_index };
          }
          if (p.is_heading || p.IsHeading) {
            groupedItems[p.option_index].productList.push({
              ...p,
              is_heading: true
            });
            return;
          }
          if(p.option_index == 1) {
             this.ids.push(p.id)
          }
          groupedItems[p.option_index].productList.push(p);
        });
        this.filteredContractItems = Object.values(groupedItems);
        this.filteredContractItems.sort((a, b) => a.option_index - b.option_index);
      } else {
        this.filteredContractItems = [];
      }
    },

    hasNotes(productList) {
      if (!productList) return false;
      return productList.some(item => item.remarks && item.remarks.trim() !== '');
    },



    async getContract(silent = false) {
  try {
    const response = await this.$axios_internal.get(`/public/contracts/${this.unique_key}/${this.access_key}`);

    if (!response.data.vertex_contract) {
      this.$router.push({ name: 'ContractDeletedNonExist' });
      return;
    }

    const contractData = response.data.vertex_contract;
    this.keyType = contractData.key_type;
    
    const hasExpiry = contractData.expiry_date && this.DaysToAcceptCounter(contractData.expiry_date) >= -1000;
    this.isExpired = contractData.contract_status === 9 || (contractData.contract_status !== 0 && hasExpiry && this.DaysToAcceptCounter(contractData.expiry_date) <= 0);

    if(response.data.vertex_contract != null && response.data.vertex_contract != undefined) {
      if(response.data.vertex_contract.shared_users != null && response.data.vertex_contract.shared_users != undefined) {
          if(response.data.vertex_contract.shared_users.length > 0) {
              response.data.vertex_contract.shared_users.forEach(shared_user => {
                  if(shared_user.shared_external_key == this.access_key) {
                    if(shared_user.is_access_revoked) 
                    this.$router.push({ name: 'ContractNoAccess' });
                        return;
                  }
              })
          }
      } 
    }

    if (!this.keyType) {
     this.$router.push({ name: 'ContractDeletedNonExist' });
     return;
    }

    if (!response.data.vertex_contract || response.data.vertex_contract.deleted_at) {
      this.$router.push({ name: 'ContractDeletedNonExist' });
      return;
    }

    // Pre-populate authority checkbox if already confirmed for this specific signer
    if (this.contract && this.contract.viewer_authority_confirmed) {
      this.authorityConfirmed = true;
    }

    if (silent && this.contract) {
      // Surgical update: update fields that change during signing/status checks
      this.contract.contract_status = response.data.vertex_contract.contract_status;
      this.contract.is_accepted = response.data.vertex_contract.is_accepted;
      this.contract.accepted_at = response.data.vertex_contract.accepted_at;
      this.contract.xodo_status = response.data.vertex_contract.xodo_status;
      this.contract.viewer_has_signed = response.data.vertex_contract.viewer_has_signed;
      this.contract.viewer_can_sign = response.data.vertex_contract.viewer_can_sign;
      
      if (response.data.vertex_contract.accepted_by_email) {
        this.contract.accepted_by_email = response.data.vertex_contract.accepted_by_email;
      }
      if (response.data.vertex_contract.shared_users) {
        this.contract.shared_users = response.data.vertex_contract.shared_users;
      }
      
      // Update signers list for the sidebar
      if (response.data.vertex_contract.contract_acceptance) {
        this.contract.contract_acceptance = response.data.vertex_contract.contract_acceptance;
      }
      
      // Ensure local contract sections are updated when accepted or signers change
      if (this.contract.is_accepted || response.data.vertex_contract.contract_acceptance) {
        this.localContractSections = [...this.parsedContractSections];
        this.reRenderKey += 1;
      }
      
    } else {
      this.contract = response.data.vertex_contract;
      this.contract_settings = response.data.contract_settings || null;

      // Initialize the local (drag-reorderable) section list from the parsed JSON
      this.localContractSections = [...this.parsedContractSections];
      this.reRenderKey += 1;
    }

    if (this.isContractExpired && this.contract.is_accepted == false) {
    this.$router.push({ 
        name: 'ContractExpired',
        params: { unique_key: this.unique_key, access_key: this.access_key } 
    });
    return;
}

    this.filterContractItems(this.contract.contract_items);

    const bdm = this.contract.bdm;
    const bdmFullName = bdm ? `${bdm.first_name || ''} ${bdm.last_name || ''}`.trim() : '';

    if (bdm && bdmFullName) {
      this.vertex_bdm = bdmFullName;
      this.vertex_bdm_email = bdm.email || '';
      this.vertex_bdm_mobile = bdm.mobile || '';
      this.vertex_bdm_calendar_link = bdm.calendar_link || '';
    } else if (this.contract.contract_owner && this.contract.contract_owner.trim()) {
      this.vertex_bdm = this.contract.contract_owner;
      this.vertex_bdm_email = this.contract.contract_owner_email || '';
      this.vertex_bdm_mobile = this.contract.contract_owner_contact || '';
      this.vertex_bdm_calendar_link = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;
    } else {
      this.vertex_bdm = "Vertex Cyber Security";
      this.vertex_bdm_email = "Martin@vtxsecurity.com";
      this.vertex_bdm_mobile = "1300 229 237";
      this.vertex_bdm_calendar_link = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;
    }

    if (this.keyType === 'internal') {
      this.IsHidden = false;
    }

    // Check for ?accept=1 in the URL — prompt authority confirmation instead of auto-signing
        if (this.$route.query.accept == '1') {
          if (!this.contract.is_accepted && !this.isContractExpired && this.canAcceptContract) {
             // Scroll to the authority checkbox and prompt the user
             this.$nextTick(() => {
               const el = document.querySelector('.authority-confirmation');
               if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
             });
             this.$toast.info('Please confirm you are authorised to sign this contract, then click "Sign Contract".');
             //Clean the URL so a refresh doesn't trigger it again
             this.$router.replace({ query: null }); 
          }
        }
  } catch (error) {
    if (error.response && error.response.status === 410) {
      this.$router.push({ name: 'ContractNoAccess', query: { reason: 'revoked' } });
    } else {
      this.$router.push({ name: 'ContractDeletedNonExist' });
    }
  } finally {
    // isLoading is now handled by the callers who actually need a spinner
    
    // Fetch dynamic revisions to re-apply visual annotations manually on the client
    if (this.contract && this.contract.id) {
      try {
        const revRes = await this.$axios_internal.get(`/public/contracts/${this.unique_key}/${this.access_key}/revisions`);
        if (revRes.data && revRes.data.revisions) {
          this.pendingSectionRevisions = revRes.data.revisions;
          this.revisionUnreadCounts = revRes.data.unread_counts || {};
          this.$nextTick(() => {
              setTimeout(() => {
                 this.reApplyVisualAnnotations(this.pendingSectionRevisions);
              }, 400);
          });
        }
      } catch(e) {
        // Could not fetch user revisions
      }

      // After loading, check if the Xodo document has been signed.
      // This handles the case where the user returns from Xodo signing
      // and the webhook hasn't fired yet (or can't reach localhost in dev).
      // REMOVED: This was causing an infinite loop. Status checks are now handled in init() and via statusPollInterval.
    }
  }

    },

    getFirstLettersOfName(contact_name) {
      const names = contact_name.split(' ');
      let initials = '';
      for (const name of names) {
        if (name.length > 0) {
          initials += name[0].toUpperCase();
        }
      }
      return initials;
    },


    async handleDeclineReissue() {
      this.isLoading = true;
      this.$toast.info("Updating contract status...");
      try {
         await this.$axios_internal.post(`/contracts/decline-reissue`, { 
           unique_key: this.unique_key, 
            access_key: this.access_key 
        });


        this.contract.contract_reissue = false;
        this.$toast.success("Your preference has been saved.");
        this.$router.push({ name: 'ContractFeedback', params: { unique_key: this.unique_key, access_key: this.access_key } })
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Could not update the contract. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },

    async downloadContractPdf() {
      this.isLoading = true;
      this.$toast.info("Generating PDF, please wait...");

      try {
        const response = await this.$axios.get(
          `/public/contracts/${this.unique_key}/${this.access_key}/pdf`,
          { responseType: 'blob' }
        );

        // Create a download link from the blob
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const sanitizedTitle = (this.contract.contract_title || 'Contract').replace(/[^a-z0-9\s]/gi, '_').replace(/\s+/g, '_');
        link.download = `${sanitizedTitle}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toast.success("PDF downloaded successfully!");
      } catch (error) {
        this.$toast.error("Failed to download PDF. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },
    openShareModal() {
      this.showShareModal = true;
    },
    closeShareModal() {
      this.showShareModal = false;
    },
    handleShareSuccess(sharedUsers) {
      this.contract.shared_users = sharedUsers;
      this.$toast.success('Contract shared successfully!');
    },
     handleShareRevoked(sharedUsers) {
      this.contract.shared_users = sharedUsers;
      this.$toast.success('User access has been successfully revoked.');
    },
    handleShareFailure(errorMessage) {
      this.$toast.error(errorMessage);
    },
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    }, 
     handleAcceptReissue() {
      if (this.contract.renewal_token) {
        const uniqueKey = this.unique_key;
        const accessKey = this.access_key;
        const token = this.contract.renewal_token;
        
        window.location.href = `/renew-contract/${uniqueKey}/${accessKey}?token=${token}`;
      } else {
        this.$toast.error("Renewal token is missing. Please contact support.");
      }
    },
    triggerReapplyAnnotations() {
      this.reRenderKey += 1;
      this.$nextTick(() => {
        setTimeout(() => {
          this.reApplyVisualAnnotations(this.pendingSectionRevisions);
        }, 100);
      });
    }
  },
  watch: {
    isEditModeEnabled() {
      this.triggerReapplyAnnotations();
    }
  },
};
</script>

<style scoped>
/* --- YOUR EXISTING LAYOUT STYLES (UNCHANGED) --- */
.contract-container {
 position: relative;
  display: flex;
  height: 100vh; /* Changed from min-height to height */
  overflow: hidden; /* Added this line */
  font-family: 'Roboto', sans-serif !important;
  color: #333;
  background-color: #f0f8ff;
  width: 100%;
}

.sidebar {
  transition: flex-basis 0.3s cubic-bezier(.4,0,.2,1), padding 0.3s cubic-bezier(.4,0,.2,1), min-width 0.3s cubic-bezier(.4,0,.2,1);
  background-color: #435666;
  padding: 20px;
  flex-basis: 250px;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  overflow: visible;
  flex-shrink: 0;
  position: relative;
}

.sidebar-logo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
}

.sidebar-logo .logo {
  max-height: 60px;
  margin-bottom: 10px;
}

.sidebar-logo hr {
  width: 100%;
  border: 1px solid #b0bec5;
  margin: 10px 0;
}

.sidebar-section {
  margin-bottom: 20px;
}

.sidebar-section h3 {
  font-size: 1em;
  margin-bottom: 8px;
  color: white;
  font-weight: bold;
}

.sidebar-section p {
  font-size: 0.9em;
  color: whitesmoke;
  border: 1px solid #b0bec5;
  padding: 10px 20px;
  border-radius: 10px;
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
}

.status-indicator {
  display: inline-block;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.9em;
  color: white;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  font-weight: bold;
  margin-top: 5px;
}

.status-indicator.active {
  background-color: #4caf50;
}

.status-indicator.not-active {
  background-color: grey;
}

/* --- NEW STATUS COLORS --- */
.status-indicator.accepted {
  background-color: green; /* Green */
}

.status-indicator.partially-accepted {
  background-color: #00a0c1; /* Vertex Teal - Indicates accepted but open for more */
}

.status-indicator.expired {
  background-color: #f3994d; /* Orange */
}

.status-indicator.declined {
  background-color: #EB1C24; /* Red */
}

.status-indicator.pending {
  background-color: #5a6e80; /* Blue-Grey for countdown/active */
}

.main-content {
  flex-grow: 1;
  padding: 25px;
  max-width: none;
  max-height: 100vh;
  overflow-y: auto;
  transition: margin-left 0.3s ease;
}

.main-content.expanded {
  margin-left: 10px; /* Or use flexbox to handle the sizing automatically */
}

.contract-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 25px;
  background-color: #fff;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(150, 131, 131, 0.05);
}

.header-text h1 {
  font-size: 1.5em;
  color: darkslategray;
  margin: 0;
}

.header-actions {
  margin-left: auto;
}

.header-actions button, .header-actions span {
  margin-left: 10px;
  padding: 8px 16px;
  border: none;
  border-radius: 0px;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.9em;
}

.header-actions .primary {
  background-color: #1976d2;
  color: white;
}

.additional-actions .primary {
  background-color: #1976d2;
  color: white;
}

.header-actions .accepted-contract {
  background-color: green;
  color: white;
}

.header-actions .secondary {
  background-color: #6c757d;
  color: white;
}

/* Authority to Sign Checkbox */
.authority-confirmation {
  width: 100%;
  margin-top: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
  border: 1px solid #b3d7f5;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(25, 118, 210, 0.08);
}

.authority-checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  margin: 0;
  user-select: none;
}

.authority-checkbox {
  margin-top: 3px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  accent-color: #1976d2;
  cursor: pointer;
}

.authority-text {
  font-size: 13.5px;
  line-height: 1.5;
  color: #37474f;
}

.authority-text strong {
  color: #1a1a1a;
}

.authority-warning {
  color: #dc3545;
  font-size: 13px;
  font-weight: 500;
  margin-top: 10px;
  margin-bottom: -4px;
  padding: 6px 10px;
  background-color: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

/* ========== Contract Signing Styles ========== */
.contract-footer-signing-container {
  margin-top: 16px;
  margin-bottom: 24px;
}

.execution-page-signing-block {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.footer-authority-box {
  width: 100%;
  margin: 0;
  padding: 15px 18px;
  background: #fafafa;
  border: 1px dashed #bbb;
  border-radius: 4px;
  box-shadow: none;
}

.authority-org-name {
  color: #00a0c1;
  font-weight: 700;
}

.footer-signing-action-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
}

.footer-sign-btn {
  background-color: #1976d2;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease, transform 0.1s ease;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.2);
}

.footer-sign-btn:hover:not(:disabled) {
  background-color: #1565c0;
  transform: translateY(-1px);
}

.footer-sign-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.footer-banner {
  padding: 16px 20px;
  margin-top: 12px;
  margin-bottom: 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  text-align: left;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.footer-banner.executed-banner {
  justify-content: space-between;
}

.contract-details {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.additional-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.testimonial-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.contract-introduction,
.product-services,
.content-blocks {
  margin-bottom: 15px;
  line-height: 1.6;
}

.contract-introduction p,
.product-services p,
.content-blocks p {
  margin-bottom: 10px;
}

.contract-introduction p:last-child,
.product-services p:last-child,
.content-blocks p:last-child {
  margin-bottom: 0;
}

.right-sidebar {
  background-color: #435666;
  padding: 25px;
   /* Replaced fixed width with flex-basis to allow scaling */
  flex-basis: 300px; /* Provides a minimum width for the right sidebar */
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100vh;
  max-height: 100vh;
  flex-shrink: 0; /* Add this line to prevent shrinking */
}

.top-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.signed-status-sidebar-badge {
  background: rgba(46, 125, 50, 0.2);
  border: 1px solid #2e7d32;
  color: #a5d6a7;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.bottom-actions {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-top: 25px;
  position: absolute;
  bottom: 25px;
  left: 0;
  right: 0;
  background-color: inherit;
  padding: 25px;
}

.right-sidebar .sidebar-section h3 {
  font-size: 1em;
  margin-bottom: 10px;
  color: white;
  font-weight: bold;
}

.right-sidebar .sidebar-section p {
  font-size: 0.9em;
  color: whitesmoke;
}

.expiry-countdown {
  color: #f3994d !important;
  border: none !important;
  padding-left: 5px !important;
  font-weight: bold;
}

.contract-accepted-sidebar-label {
  color: rgba(25, 135, 84, 1.0) !important;
  border: none !important;
  padding-left: 5px !important;
  font-weight: bold;
}

.top-actions .sign-contract-btn {
  background-color: #1976d2;
  color: white;
  width: 100%;
  padding: 12px 18px;
  border-radius: 6px !important;
  font-weight: bold;
  font-size: 0.95em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;
}

.top-actions .sign-contract-btn:hover:not(:disabled) {
  background-color: #1565c0;
}

.top-actions .sign-contract-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.top-actions .share {
  background-color: #00a0c1;
  color: white;
}

.top-actions .book-call {
  background-color: rgba(25, 135, 84, 1.0);
  color: white;
}

.top-actions .ask-question {
  background-color: #ffa000;
  color: white;
}

.bottom-actions .log-in-button {
  width: 100%;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  background-color: #00a0c1;
  color: white;
  font-weight: bold;
  cursor: pointer;
  font-size: 0.95em;
  margin-top: 0;
}

.bottom-actions .view-sample-contract {
  width: 100%;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  background-color: grey;
  color: white;
  font-weight: bold;
  cursor: pointer;
  font-size: 0.95em;
  margin-top: 0;
}

.button {
  padding: 10px 18px;
  border: none;
  border-radius: 6px !important;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.95em;
}

/* Scroll bar */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #00a0c1;
}

::-webkit-scrollbar-thumb:hover {
  background: #00a0c1;
}


/* --- PRODUCT TABLE STYLES --- */
.product-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  border: 1px solid #ddd;
}

.product-table th,
.product-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  vertical-align: top;
}

.product-table th {
  background-color: #00bcd4;
  color: white;
  font-weight: bold;
  vertical-align: middle;
}

.product-table tr:last-child td {
  border-bottom: none;
}

.total-row {
  font-weight: bold;
  background-color: #f9f9f9;
}

.total-label {
  text-align: right;
  padding-right: 8px;
}

.total-amount {
  text-align: right;
}

/* --- TAB SPECIFIC STYLES --- */
.tab-wrapper {
  margin: 30px auto;
  max-width: 100%;
  padding: 0 15px;
}

.nav-tabs {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
}

.nav-tabs .nav-link {
  border-radius: 0 !important;
  border: 1px ridge grey;
  border-bottom-color: #dee2e6;
  margin-bottom: -1px;
  background-color: #e9ecef;
  color: #495057;
  font-weight: 500;
  padding: 10px 15px;
  cursor: pointer;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  font-weight: bold;
}

.tab-content {
  border: 1px solid #dee2e6;
  border-top: none;
  padding: 20px;
  background-color: #fff;
}

.content-blocks.image-grid-section {
  max-width: 100%;
}

/* Preserve the WYSIWYG table-based grid layout for images */
.content-blocks.image-grid-section :deep(table) {
  display: table !important;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.content-blocks.image-grid-section :deep(td) {
  padding: 8px;
  vertical-align: middle;
  text-align: center;
}

.content-blocks.image-grid-section :deep(img) {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}


/* --- MODAL STYLES --- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Generic modal content for simple modals like "Book a Call" */
.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
}
.modal-content h3 {
    margin-bottom: 20px;
    font-size: 1.5em;
    color: #333;
}
.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 2em;
  color: #888;
  cursor: pointer;
}

/* --- PDF SPECIFIC STYLES (REVISED) --- */
#pdf-content-area {
    position: absolute;
    left: -9999px;
    top: 0; 
    width: 210mm;
    padding: 20mm;
    box-sizing: border-box; 
    background-color: white; 
    font-family: 'Helvetica', 'Arial', sans-serif; /* Use PDF-safe fonts */
    font-size: 11pt; 
    line-height: 1.5;
    color: #333333;
}

#pdf-content-area p,
#pdf-content-area li,
#pdf-content-area .pdf-product-table tr {
    page-break-inside: avoid !important;
}

#pdf-content-area h1, #pdf-content-area h2, #pdf-content-area h3, #pdf-content-area h4 {
    color: #00a0c1;
    line-height: 1.2;
    page-break-inside: avoid !important;
    page-break-after: avoid !important; 
}


/* --- UNIFIED MODAL DESIGN SYSTEM --- */
.sample-contract-modal {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
}

.sample-contract-modal {
  max-width: 550px;
}

.proceed-modal-custom {
  max-width: 650px;
}

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.modal-title-custom {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  color: #333;
}

.close-button-custom {
  background: none;
  border: none;
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  opacity: 0.7;
}
.close-button-custom:hover {
  opacity: 1;
}

.modal-controls {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-body-custom {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
  background-color: #fbfbfb;
}

.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
  background-color: #f8f9fa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}


/* Specific styles for Sample Contracts list */
.contract-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.contract-item-card {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.contract-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.08);
}
.contract-name {
  font-weight: 600;
}
.contract-details {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Specific styles for Proceed to Contract modal body */
.items-selection-wrapper {
  margin-top: 0; /* Removed margin as padding is handled by body */
}
.items-selection-wrapper p {
  margin-bottom: 10px;
  font-weight: 500;
}
.proceed-modal-custom .form-group {
  margin-bottom: 20px;
}
.proceed-modal-custom label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}
.proceed-modal-custom .form-control {
  width: 100%;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
}
.item-list {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px;
  background-color: #fff;
}
.item-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 5px;
}
.item-row:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}
.item-selection {
  padding-right: 12px;
  padding-top: 2px;
}
.item-details {
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  gap: 15px;
}
.item-name-desc {
  display: flex;
  flex-direction: column;
}
.item-description {
  font-size: 0.85em;
  color: #6c757d;
  margin-top: 4px;
}
.item-price {
  font-weight: 500;
  white-space: nowrap;
}
.item-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00a0c1;
}
.item-row label {
  margin-bottom: 0;
  cursor: pointer;
  font-weight: normal;
}
.select-all-row {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 5px;
  align-items: center;
}
.contract-summary {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}
.contract-summary h4 {
  margin-bottom: 10px;
  font-size: 1.1em;
  color: #333;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 0.95em;
}
.summary-row.total {
  font-size: 1.1em;
  border-top: 1px solid #ddd;
  margin-top: 5px;
  padding-top: 10px;
}
.no-results {
  padding: 2rem;
  text-align: center;
  color: #6c757d;
}

/* --- MISC STYLES --- */
.expired-content-wrapper, .deleted-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 200px);
  text-align: center;
}
.toast {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 8px;
  color: white;
  z-index: 2000;
}
.grey {
  background-color: #ced4da !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
}
.sidebar.collapsed {
  flex-basis: 50px;
  min-width: 50px;
  padding: 10px 5px;
}
.sidebar.collapsed .sidebar-section,
.sidebar.collapsed .sidebar-logo hr,
.sidebar.collapsed .sidebar-logo .logo {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.sidebar .sidebar-section,
.sidebar .sidebar-logo hr,
.sidebar .sidebar-logo .logo {
  transition: opacity 0.2s ease 0.1s;
}
.sidebar.collapsed .sidebar-logo .collapsed-logo {
  opacity: 1;
  pointer-events: auto;
}
.collapse-btn {
  position: absolute;
  top: 50%;
  right: -15px;
  transform: translateY(-50%);
  background-color: #00a0c1;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  z-index: 1000;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  padding: 0;
  line-height: 1;
  box-shadow: 0 0 8px rgba(0, 160, 193, 0.5), 0 2px 4px rgba(0,0,0,0.15);
}
.collapse-btn:hover {
  background-color: #008aa6;
  box-shadow: 0 0 12px rgba(0, 160, 193, 0.7), 0 2px 6px rgba(0,0,0,0.2);
}

/* --- Hamburger menu button --- */
.mobile-menu-btn {
  display: none;
  background: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 1.1em;
  color: #435666;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}
.mobile-menu-btn:hover {
  background-color: #f0f0f0;
}

/* --- Mobile drawer backdrop --- */
.mobile-drawer-backdrop {
  display: none;
}

/* ========================================================================
   MOBILE RESPONSIVE
   ======================================================================== */

@media (max-width: 1024px) {
  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 280px;
    flex-basis: 280px;
    z-index: 1100;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }
  .sidebar.mobile-drawer-open {
    transform: translateX(0);
  }
  .collapse-btn {
    display: none;
  }
  .mobile-drawer-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1050;
  }
}

@media (max-width: 768px) {
  .contract-container {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: visible;
    width: 100%;
    box-sizing: border-box;
  }
  .main-content {
    padding: 12px;
    max-height: none;
    overflow-y: visible;
    overflow-x: hidden;
    width: 100%;
    box-sizing: border-box;
  }
  .right-sidebar {
    flex-basis: auto;
    width: 100%;
    min-height: auto;
    max-height: none;
    padding: 20px 15px;
    box-sizing: border-box;
  }
  .contract-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
  }
  .header-actions {
    margin-left: 0;
    display: flex;
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
  }
  .header-actions button,
  .header-actions span {
    margin-left: 0;
    flex: 1;
    min-width: 120px;
    text-align: center;
  }
  .contract-sections-wrapper {
    width: 100%;
    box-sizing: border-box;
    overflow-x: visible;
  }
  .contract-section-block {
    padding: 16px;
    margin-bottom: 16px;
    width: 100%;
    box-sizing: border-box;
    background: #fff;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .section-content-wrapper {
    width: 100%;
    box-sizing: border-box;
  }
  .product-table {
    min-width: 600px;
    width: 100%;
  }
  .product-table th,
  .product-table .total-label,
  .product-table .total-amount {
    white-space: nowrap;
  }
  .simple-pricing-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    box-sizing: border-box;
  }
  /* Schedule table formatting on mobile */
  #schedule_table_block table,
  .contract-section-block[data-section-type="ScheduleTable"] table {
    min-width: 580px;
  }
  #schedule_table_block th,
  #schedule_table_block td:first-child,
  .contract-section-block[data-section-type="ScheduleTable"] th,
  .contract-section-block[data-section-type="ScheduleTable"] td:first-child {
    white-space: nowrap;
  }
  /* Inner pricing table inside Schedule table */
  #schedule_table_block table table,
  .contract-section-block[data-section-type="ScheduleTable"] table table {
    min-width: 440px;
  }
  #schedule_table_block table table th,
  #schedule_table_block table table td:nth-child(2),
  #schedule_table_block table table td:nth-child(3),
  #schedule_table_block table table tfoot td,
  .contract-section-block[data-section-type="ScheduleTable"] table table th,
  .contract-section-block[data-section-type="ScheduleTable"] table table td:nth-child(2),
  .contract-section-block[data-section-type="ScheduleTable"] table table td:nth-child(3),
  .contract-section-block[data-section-type="ScheduleTable"] table table tfoot td {
    white-space: nowrap;
  }
  /* Execution page signature blocks stack vertically on mobile */
  #execution_page_block table,
  #execution_page_block tbody,
  #execution_page_block tr,
  #execution_page_block td,
  .contract-section-block[data-section-type="ExecutionPage"] table,
  .contract-section-block[data-section-type="ExecutionPage"] tbody,
  .contract-section-block[data-section-type="ExecutionPage"] tr,
  .contract-section-block[data-section-type="ExecutionPage"] td {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  #execution_page_block td,
  .contract-section-block[data-section-type="ExecutionPage"] td {
    margin-bottom: 16px !important;
    padding: 16px !important;
  }
}


/* --- NEW: STYLES FOR 'SHARED WITH' SECTION --- */
.shared-user-list {
  border: 1px solid #b0bec5;
  padding: 10px 15px;
  border-radius: 10px;
  color: whitesmoke;
  font-size: 0.9em;
  display: flex;
  flex-direction: column;
  gap: 8px; /* Adds space between each user item */
}

.shared-user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px; /* Space between email and role */
}

.user-email {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; /* Adds '...' if the email is too long */
}

.user-role {
  text-transform: capitalize;
  background-color: #5a6e80; /* A slightly darker shade from the sidebar */
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  flex-shrink: 0; /* Prevents the role badge from shrinking */
}

.jump-to-pricing-btn {
  margin-top: 8px;
  background: none;
  border: 1px solid #00a0c1;
  color: #00a0c1;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.jump-to-pricing-btn:hover {
  background-color: #00a0c1;
  color: white;
}

/* Ensure the header text displays items in a column */
.header-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* ========== JSON Contract Sections ========== */
.contract-sections-wrapper {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.reorder-hint {
  font-size: 12px;
  color: #888;
  padding: 6px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px dashed #ccc;
  display: flex;
  align-items: center;
  gap: 8px;
}

.contract-section-block {
  position: relative;
  padding: 28px 32px;
  margin-bottom: 24px;
  border-bottom: 1px solid #eee;
  border-radius: 6px;
  background: #fff;
  transition: background 0.15s;
  box-sizing: border-box;
  width: 100%;
}

.contract-section-block:hover {
  background: #fafcff;
}

.contract-section-block.section-hidden {
  opacity: 0.45;
  background: #fef6f6;
}

.contract-section-block.section-has-revision {
  border-left: 3px solid #ff9800;
}

.contract-section-block.section-restricted {
  position: relative;
  cursor: not-allowed;
  user-select: none;
  -webkit-user-select: none;
}
.contract-section-block.section-restricted::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(240, 240, 240, 0.35);
  pointer-events: none;
  border-radius: 8px;
}
.contract-section-block.section-restricted .section-content-wrapper {
  pointer-events: none;
}
.contract-section-block[draggable="true"] {
  cursor: grab;
}
.contract-section-block[draggable="true"]:active {
  cursor: grabbing;
}

.drag-handle {
  position: absolute;
  top: 12px;
  left: 8px;
  color: #ccc;
  font-size: 14px;
  cursor: grab;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}
.contract-section-block:hover .drag-handle {
  color: #aaa;
}

.section-content-wrapper {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.section-hidden-badge,
.section-revision-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  margin-bottom: 10px;
}

.section-hidden-badge {
  background: #fdecea;
  color: #c62828;
}

.section-revision-badge {
  background: #fff3e0;
  color: #e65100;
}

.section-html-content {
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.7;
  color: #333;
}

.section-html-content p {
  margin-bottom: 12px;
  word-break: normal;
  overflow-wrap: break-word;
}

.section-html-content a {
  color: #00a0c1;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.section-html-content table {
  width: 100%;
  border-collapse: collapse;
}

.section-html-content th,
.section-html-content td {
  word-break: normal;
  overflow-wrap: normal;
}

/* Schedule table outer column stabilization */
#schedule_table_block > table > thead > tr > th:first-child,
#schedule_table_block > table > tbody > tr > td:first-child,
.contract-section-block > .section-content-wrapper > .section-html-content table > thead > tr > th:first-child,
.contract-section-block > .section-content-wrapper > .section-html-content table > tbody > tr > td:first-child {
  width: 75px;
  min-width: 65px;
  white-space: nowrap;
}

#schedule_table_block > table > thead > tr > th:nth-child(2),
#schedule_table_block > table > tbody > tr > td:nth-child(2),
.contract-section-block > .section-content-wrapper > .section-html-content table > thead > tr > th:nth-child(2),
.contract-section-block > .section-content-wrapper > .section-html-content table > tbody > tr > td:nth-child(2) {
  width: 175px;
  min-width: 140px;
}

/* Inner pricing table column stabilization */
.section-html-content tfoot {
  display: table-row-group;
}
.section-html-content table table th:nth-child(2),
.section-html-content table table th:nth-child(3),
.section-html-content table table td:nth-child(2),
.section-html-content table table td:nth-child(3),
.section-html-content table table tfoot td {
  white-space: nowrap;
  text-align: right;
  width: 120px;
  min-width: 100px;
}

/* Action bar — slides up on hover */
.section-action-bar {
  display: flex;
  gap: 8px;
  padding-top: 14px;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s, transform 0.2s;
  pointer-events: none;
  border-top: 1px solid #f0f0f0;
  margin-top: 14px;
}

.contract-section-block:hover .section-action-bar {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.section-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  transition: background 0.15s, transform 0.1s;
}
.section-action-btn:hover { transform: translateY(-1px); }

.section-action-btn.comment  { background: #e3f2fd; color: #0d47a1; }
.section-action-btn.revision { background: #e8f5e9; color: #1b5e20; }
.section-action-btn.delete   { background: #fdecea; color: #b71c1c; }

/* Section action modal */
.section-action-modal {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px;
  width: 560px;
  max-width: 95vw;
  position: relative;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}

.section-action-title {
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  color: #00a0c1;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-action-subtitle {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
}

.section-delete-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #856404;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.section-action-textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.section-action-textarea:focus { border-color: #00a0c1; }

.section-action-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

/* ===== Strikethrough annotation ===== */
.section-struck {
  text-decoration: line-through;
  opacity: 0.6;
  position: relative;
}
.section-struck::after {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 4px,
    rgba(220,38,38,0.08) 4px,
    rgba(220,38,38,0.08) 6px
  );
  pointer-events: none;
}

/* ===== New action button colours ===== */
.section-action-btn.strikethrough {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}
.section-action-btn.strikethrough:hover {
  background: #fde68a;
}
.section-action-btn.strikethrough-comment {
  background: #fff7ed;
  color: #c2410c;
  border-color: #fdba74;
}
.section-action-btn.strikethrough-comment:hover {
  background: #fed7aa;
}

/* ===== Annotation Toolbar ===== */
.annotation-toolbar {
  position: absolute;
  z-index: 1000;
  display: flex;
  background: #333;
  padding: 6px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  gap: 8px;
}
.annotation-toolbar::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}
.annotation-toolbar button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.annotation-toolbar button:hover {
  background: rgba(255,255,255,0.2);
}

/* ===== Edit Mode Toggle ===== */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
}
.switch input { 
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}
.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}
input:checked + .slider {
  background-color: #00a0c1;
}
input:focus + .slider {
  box-shadow: 0 0 1px #00a0c1;
}
input:checked + .slider:before {
  transform: translateX(22px);
}
.slider.round {
  border-radius: 24px;
}
.slider.round:before {
  border-radius: 50%;
}

/* ===== PDF Toolbar Styles ===== */
.pdf-mode-active {
  padding-top: 0 !important;
  display: flex;
  flex-direction: column;
}
.pdf-toolbar {
  background: #323639;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.3);
  z-index: 1000;
  position: sticky;
  top: 0;
  width: 100%;
  box-sizing: border-box;
}
.pdf-toolbar-main-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.pdf-toolbar-mode-badge {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.pdf-toolbar-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.pdf-toolbar .divider {
  width: 1px;
  height: 20px;
  background-color: #5f6368;
  margin: 0 4px;
}
.pdf-toolbar button {
  background: #44484b;
  border: 1px solid transparent;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #f1f3f4;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  touch-action: manipulation;
}
.pdf-toolbar button:hover:not(.disabled) {
  background: #5a5f63;
  border-color: rgba(255,255,255,0.2);
}
.pdf-toolbar button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pdf-toolbar .btn-undo {
  background: #c62828;
  color: #fff;
  border: 1px solid #e53935;
}
.pdf-toolbar .btn-undo:hover {
  background: #b71c1c;
}
.pdf-toolbar .btn-strike { color: #ff8a80; }
.pdf-toolbar .btn-comment { color: #ffe57f; }
.pdf-toolbar .btn-strike-comment { color: #ffab40; }

.toolbar-hint {
  color: #aaa;
  font-style: italic;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.toolbar-action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .pdf-toolbar {
    padding: 8px 10px;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .pdf-toolbar-main-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 6px;
  }
  .pdf-toolbar-actions-row {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .toolbar-action-buttons {
    width: 100%;
    display: flex;
    gap: 6px;
  }
  .toolbar-action-buttons button {
    flex: 1;
    padding: 6px 4px;
    font-size: 12px;
    text-align: center;
    justify-content: center;
  }
  .pdf-toolbar .divider {
    display: none;
  }
}
/* --- DYNAMIC HIGHLIGHT STYLES --- */
:deep(.strike-redline) {
  text-decoration: line-through;
  color: #c0392b;
  background-color: #fadbd8;
}
:deep(.comment-highlight) {
  background-color: #f1c40f;
  cursor: help;
  border-bottom: 2px dashed #d35400;
  position: relative;
}
:deep(.strike-comment-highlight) {
  text-decoration: line-through;
  color: #c0392b;
  background-color: #f1c40f;
  cursor: help;
  border-bottom: 2px dashed #d35400;
  position: relative;
}
:deep(.comment-highlight:hover::after),
:deep(.strike-comment-highlight:hover::after) {
  content: attr(data-comment);
  position: absolute;
  background: #333;
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  top: 100%;
  left: 0;
  margin-top: 6px;
  white-space: nowrap;
  z-index: 20;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  text-decoration: none; 
}
</style>
